import React, {
  Children,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import {
  addDoc,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  limit,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
  where,
} from "firebase/firestore";

import { onAuthStateChanged } from "firebase/auth";
import Select, { components } from "react-select";
import { ReactSortable } from "react-sortablejs";

import { auth, db } from "../../Security/firebase-config";
import { useTeam } from "../../context/TeamContext";
import {
  feedbackReportsCollection,
  lfgChatDoc,
  lfgChatMessagesCollection,
  lfgChatsCollection,
  lfgPostDoc,
  lfgPostsCollection,
} from "../../utils/communityFirestorePaths";
import {
  LFG_SAVED_POSTS_STORAGE_KEY,
  LFG_SETTINGS_STORAGE_KEY,
  LFG_SETTINGS_UPDATED_EVENT,
  readLfgSettings,
  readSavedPostIds,
  saveLfgSettings,
} from "./lfgSettingsStorage";

import DeleteHighlightConfirm from "../../components/core-components/DeleteHighlightConfirm";
import CustomAlert from "../../components/core-components/CustomAlert";
import { AGENTS, TIER_TITLES } from "../../components/team-voting/agentTiers";

import "./LFG.css";

/* ======================================================================
   CONSTANTS
   ====================================================================== */

const ROLES = [
  "Duelist",
  "Scan Initiator",
  "Flash Initiator",
  "Smokes",
  "Secondary Smokes",
  "Sentinel",
  "IGL",
  "Mid Rounder",
  "Coach",
  "Analyst",
  "Manager",
  "Sub",
];

const RANKS = [
  "Iron I",
  "Iron II",
  "Iron III",
  "Bronze I",
  "Bronze II",
  "Bronze III",
  "Silver I",
  "Silver II",
  "Silver III",
  "Gold I",
  "Gold II",
  "Gold III",
  "Platinum I",
  "Platinum II",
  "Platinum III",
  "Diamond I",
  "Diamond II",
  "Diamond III",
  "Ascendant I",
  "Ascendant II",
  "Ascendant III",
  "Immortal I",
  "Immortal II",
  "Immortal III",
  "Radiant",
];

const REGIONS = [
  "NA-E",
  "NA-W",
  "LATAM-N",
  "LATAM-S",
  "Brazil",
  "IBIT",
  "France",
  "North Europe",
  "DACH",
  "EMEA-E",
  "Türkiye",
  "Middle East",
  "MEA",
  "South-Asia",
  "South-Korea",
  "Japan",
  "APAC",
  "OCE",
  "CH",
];

const TEAM_TYPES = [
  "For fun",
  "Ranked",
  "Premier Open",
  "Premier Intermediate",
  "Premier Elite",
  "Premier Contender",
  "Collegiate",
  "Tier 3 or 4",
  "Tier 2",
  "Tier 1",
];

const REPORT_REASONS = [
  "Spam / advertising",
  "Harassment / hate",
  "Scam / phishing",
  "Inappropriate content",
  "Impersonation",
  "Other",
];

const NAV_ITEMS = [
  { id: "feed", label: "LFG Feed", icon: "⌂" },
  { id: "my-posts", label: "My Posts", icon: "▤" },
  { id: "messages", label: "Messages", icon: "✉" },
  { id: "saved", label: "Saved", icon: "☆" },
  { id: "settings", label: "Settings", icon: "⚙" },
];

/* ======================================================================
   UTILITIES
   ====================================================================== */

const toOptions = (arr) =>
  arr.map((value) => ({
    label: value,
    value,
  }));

const prettyFirestoreError = (error) => {
  const code = error?.code || "";

  if (code === "permission-denied") {
    return "You do not have permission to do that.";
  }

  if (code === "unauthenticated") {
    return "Please log in first.";
  }

  return error?.message || "Something went wrong.";
};

const clampChars = (value, maximum) =>
  String(value || "").slice(0, maximum);

const clampWords = (value, maximum) => {
  const clean = (value || "").trim();

  if (!clean) {
    return "";
  }

  const words = clean.split(/\s+/);

  if (words.length <= maximum) {
    return clean;
  }

  return words.slice(0, maximum).join(" ");
};

// Retained for nearby date-formatting work.
// eslint-disable-next-line no-unused-vars
const formatLocalDateTime = (timestampOrDate) => {
  if (!timestampOrDate) {
    return "—";
  }

  const date = timestampOrDate?.toDate?.()
    ? timestampOrDate.toDate()
    : timestampOrDate instanceof Date
      ? timestampOrDate
      : null;

  if (!date) {
    return "—";
  }

  return new Intl.DateTimeFormat(undefined, {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
};

const formatRelativeActivity = (timestamp) => {
  const date = timestamp?.toDate?.();

  if (!date) {
    return "";
  }

  const difference = Date.now() - date.getTime();

  const minute = 60 * 1000;
  const hour = 60 * minute;
  const day = 24 * hour;

  if (difference < minute) {
    return "now";
  }

  if (difference < hour) {
    return `${Math.floor(difference / minute)}m`;
  }

  if (difference < day) {
    return `${Math.floor(difference / hour)}h`;
  }

  return `${Math.floor(difference / day)}d`;
};

const mondayOf = (value) => {
  const date = new Date(value);
  const day = (date.getDay() + 6) % 7;

  date.setDate(date.getDate() - day);
  date.setHours(0, 0, 0, 0);

  return date;
};

const stopSpacePropagation = (event) => {
  if (event.key === " ") {
    event.stopPropagation();
  }
};

const normalizeHttpUrl = (rawValue) => {
  try {
    const parsed = new URL(rawValue);

    if (
      parsed.protocol === "https:" ||
      parsed.protocol === "http:"
    ) {
      return parsed.toString();
    }
  } catch (_) {
    return null;
  }

  return null;
};

const isDiscordLabel = (label = "") => {
  const normalized = label.trim().toLowerCase();

  return (
    normalized === "discord" ||
    normalized === "discord username"
  );
};

const initialsFromName = (name = "?") => {
  const words = String(name)
    .trim()
    .split(/\s+/)
    .filter(Boolean);

  if (!words.length) {
    return "?";
  }

  return words
    .slice(0, 2)
    .map((word) => word.charAt(0).toUpperCase())
    .join("");
};

const sanitizeTiers = (tiersObject = {}) => {
  const seen = new Set();
  const output = {};

  TIER_TITLES.forEach((title) => {
    const raw = Array.isArray(tiersObject[title])
      ? tiersObject[title]
      : [];

    const validAgents = raw.filter((agent) =>
      AGENTS.includes(agent)
    );

    const deduped = [];

    validAgents.forEach((agent) => {
      if (!seen.has(agent)) {
        deduped.push(agent);
        seen.add(agent);
      }
    });

    output[title] =
      title === "My Top 3 Agents"
        ? deduped.slice(0, 3)
        : deduped;
  });

  return output;
};

const computeUnassignedFromTiers = (tiersObject = {}) => {
  const assigned = new Set(
    Object.values(tiersObject).flat()
  );

  return AGENTS.filter((agent) => !assigned.has(agent));
};

const blankTiers = () =>
  Object.fromEntries(
    TIER_TITLES.map((title) => [title, []])
  );

const postDisplayName = (post) =>
  post?.type === "team"
    ? post.teamName || "Team"
    : post?.username || "Player";

const postRegion = (post) =>
  post?.type === "team"
    ? post.teamRegion
    : post.userRegion;

const postRank = (post) =>
  post?.type === "team"
    ? post.currentAvgRank
    : post.currentRank;

const trimText = (value) =>
  typeof value === "string"
    ? value.trim()
    : "";

const pickFirstText = (...values) => {
  for (const value of values) {
    const next = trimText(value);

    if (next) {
      return next;
    }
  }

  return "";
};

const displayNameFromSeed = (value) => {
  if (!value || typeof value !== "object") {
    return "";
  }

  const byField = pickFirstText(
    value.username,
    value.userName,
    value.displayName,
    value.name,
    value.nickname
  );

  if (byField) {
    return byField;
  }

  if (
    typeof value.email === "string" &&
    value.email.includes("@")
  ) {
    return value.email.split("@")[0];
  }

  return "";
};

const memberUidFromSeed = (value) => {
  if (!value || typeof value !== "object") {
    return "";
  }

  return pickFirstText(
    value.uid,
    value.userId,
    value.id,
    value.memberId,
    value.memberUid,
    value.memberUID,
    value.ref
  );
};

const roleFromSeed = (value) => {
  if (!value || typeof value !== "object") {
    return "";
  }

  return pickFirstText(
    value.role,
    value.teamRole,
    value.memberRole,
    value.title
  );
};

const gatherUserTeamIds = (
  userData = {},
  activeTeamId = null
) => {
  const ids = new Set();

  const add = (value) => {
    const next =
      typeof value === "string"
        ? value.trim()
        : "";

    if (next) {
      ids.add(next);
    }
  };

  const addMaybeTeamRef = (value) => {
    if (typeof value === "string") {
      add(value);
      return;
    }

    if (
      value &&
      typeof value === "object"
    ) {
      add(
        value.id ||
          value.teamId ||
          value.ref
      );
    }
  };

  (userData.teamIds || []).forEach(add);
  (userData.teams || []).forEach(
    addMaybeTeamRef
  );
  (userData.memberships || []).forEach(
    addMaybeTeamRef
  );

  if (
    userData.teamsMap &&
    typeof userData.teamsMap ===
      "object"
  ) {
    Object.keys(userData.teamsMap).forEach(
      add
    );
  }

  if (
    userData.memberships &&
    typeof userData.memberships ===
      "object" &&
    !Array.isArray(userData.memberships)
  ) {
    Object.keys(
      userData.memberships
    ).forEach(add);
  }

  add(userData.activeTeamId);
  add(userData.pendingActiveTeamId);
  add(userData.teamId);
  add(activeTeamId);

  return Array.from(ids);
};

const normalizeQuestionList = (
  questions = []
) =>
  Array.isArray(questions)
    ? questions
        .map((question) =>
          trimText(
            clampChars(
              typeof question ===
                "string"
                ? question
                : question?.question ||
                    question?.text ||
                    "",
              250
            )
          )
        )
        .filter(Boolean)
        .slice(0, 5)
    : [];

const collectTeamMemberSeeds = (
  teamDoc = {},
  fallbackMembers = []
) => {
  const merged = new Map();

  const mergeMember = (
    uid,
    username,
    role
  ) => {
    const normalizedUid =
      trimText(uid);
    const normalizedUsername =
      trimText(username);
    const normalizedRole =
      trimText(role);

    if (
      !normalizedUid &&
      !normalizedUsername
    ) {
      return;
    }

    const key = normalizedUid
      ? `uid:${normalizedUid}`
      : `name:${normalizedUsername.toLowerCase()}`;

    const existing =
      merged.get(key) || {};

    merged.set(key, {
      uid:
        existing.uid ||
        normalizedUid ||
        null,
      username:
        existing.username ||
        normalizedUsername ||
        "",
      role:
        existing.role ||
        normalizedRole ||
        "Player",
    });
  };

  const pushSeed = (
    seed,
    fallbackRole = "Player"
  ) => {
    if (typeof seed === "string") {
      mergeMember(
        seed,
        "",
        fallbackRole
      );
      return;
    }

    if (
      !seed ||
      typeof seed !== "object"
    ) {
      return;
    }

    mergeMember(
      memberUidFromSeed(seed),
      displayNameFromSeed(seed),
      roleFromSeed(seed) ||
        fallbackRole
    );
  };

  (
    Array.isArray(teamDoc.members)
      ? teamDoc.members
      : []
  ).forEach((member) =>
    pushSeed(member)
  );

  (
    Array.isArray(teamDoc.teamMembers)
      ? teamDoc.teamMembers
      : []
  ).forEach((member) =>
    pushSeed(member)
  );

  (
    Array.isArray(fallbackMembers)
      ? fallbackMembers
      : []
  ).forEach((member) =>
    pushSeed(member)
  );

  (
    Array.isArray(
      teamDoc.memberUsernames
    )
      ? teamDoc.memberUsernames
      : []
  ).forEach((username) =>
    mergeMember(
      "",
      username,
      "Player"
    )
  );

  (
    Array.isArray(teamDoc.usernames)
      ? teamDoc.usernames
      : []
  ).forEach((username) =>
    mergeMember(
      "",
      username,
      "Player"
    )
  );

  const teamRoles =
    teamDoc.teamRoles &&
    typeof teamDoc.teamRoles ===
      "object"
      ? teamDoc.teamRoles
      : {};

  Object.entries(teamRoles).forEach(
    ([uid, roleValue]) => {
      mergeMember(
        uid,
        "",
        pickFirstText(
          roleValue?.role,
          roleValue
        ) || "Player"
      );
    }
  );

  if (teamDoc.teamLeaderId) {
    mergeMember(
      teamDoc.teamLeaderId,
      "",
      pickFirstText(
        teamRoles?.[
          teamDoc.teamLeaderId
        ]?.role,
        teamRoles?.[
          teamDoc.teamLeaderId
        ],
        "Leader"
      )
    );
  }

  return Array.from(
    merged.values()
  );
};

const collectTeamMemberIds = (
  teamDoc = {},
  fallbackMembers = []
) =>
  collectTeamMemberSeeds(
    teamDoc,
    fallbackMembers
  )
    .map((member) => member.uid)
    .filter(Boolean);

const buildTeamRoster = async (
  teamDoc = {},
  fallbackMembers = []
) => {
  const seeds =
    collectTeamMemberSeeds(
      teamDoc,
      fallbackMembers
    );

  const teamRoles =
    teamDoc.teamRoles &&
    typeof teamDoc.teamRoles ===
      "object"
      ? teamDoc.teamRoles
      : {};

  const roster = await Promise.all(
    seeds.map(
      async (seed) => {
        let username =
          seed.username || "";

        if (seed.uid) {
          try {
            const userSnapshot =
              await getDoc(
                doc(
                  db,
                  "uids",
                  seed.uid
                )
              );

            if (
              userSnapshot.exists()
            ) {
              const userData =
                userSnapshot.data();

              username =
                pickFirstText(
                  userData.username,
                  userData.userName,
                  userData.displayName,
                  userData.name,
                  username
                ) || username;
            }
          } catch (_) {
            // Fall back to the team
            // document value.
          }
        }

        return {
          uid: seed.uid || null,
          username:
            username || "Unknown",
          role:
            seed.role ||
            pickFirstText(
              teamRoles?.[seed.uid]
                ?.role,
              teamRoles?.[seed.uid]
            ) ||
            "Player",
        };
      }
    )
  );

  return roster
    .filter(
      (member) =>
        (
          member.role || ""
        ).toLowerCase() !==
        "spectator"
    )
    .sort((first, second) =>
      (first.username || "").localeCompare(
        second.username || "",
        undefined,
        { sensitivity: "base" }
      )
    );
};

const collectPostIdentityUids = (
  post = {}
) => {
  const ids = new Set();

  const add = (value) => {
    const next =
      typeof value === "string"
        ? value.trim()
        : "";

    if (next) {
      ids.add(next);
    }
  };

  add(post.userId);
  add(post.createdBy);

  (
    Array.isArray(post.teamMembers)
      ? post.teamMembers
      : []
  ).forEach((member) =>
    add(memberUidFromSeed(member))
  );

  return Array.from(ids);
};

const applyLiveIdentityToPost = (
  post,
  liveUsernamesByUid = {}
) => {
  if (!post || typeof post !== "object") {
    return post;
  }

  if (post.type === "user") {
    const nextUsername =
      pickFirstText(
        liveUsernamesByUid?.[
          post.userId
        ],
        post.username
      ) || "Player";

    return nextUsername ===
      (post.username || "Player")
      ? post
      : {
          ...post,
          username: nextUsername,
        };
  }

  if (
    post.type === "team" &&
    Array.isArray(post.teamMembers)
  ) {
    let changed = false;

    const nextMembers =
      post.teamMembers.map((member) => {
        if (
          !member ||
          typeof member !== "object"
        ) {
          return member;
        }

        const nextUsername =
          pickFirstText(
            liveUsernamesByUid?.[
              memberUidFromSeed(member)
            ],
            member.username,
            member.userName,
            member.displayName,
            member.name
          ) || "Unknown";

        if (
          nextUsername ===
          member.username
        ) {
          return member;
        }

        changed = true;

        return {
          ...member,
          username: nextUsername,
        };
      });

    return changed
      ? {
          ...post,
          teamMembers: nextMembers,
        }
      : post;
  }

  return post;
};

const canViewerSeePost = (
  post,
  viewerUid,
  viewerTeamIds = []
) => {
  if (!post) {
    return false;
  }

  if (
    post.type !== "user" ||
    post.userId === viewerUid ||
    !post.hideFromMyTeams
  ) {
    return true;
  }

  const hiddenTeamIds = new Set(
    Array.isArray(post.hiddenFromTeamIds)
      ? post.hiddenFromTeamIds
      : []
  );

  if (!hiddenTeamIds.size) {
    return true;
  }

  return !viewerTeamIds.some((teamId) =>
    hiddenTeamIds.has(teamId)
  );
};

const canManagePost = (
  post,
  uid,
  currentTeamId
) => {
  if (!uid || !post) {
    return false;
  }

  if (post.type === "user") {
    return post.userId === uid;
  }

  if (post.type === "team") {
    if (post.createdBy === uid) {
      return true;
    }

    return (
      !!currentTeamId &&
      post.teamId === currentTeamId
    );
  }

  return false;
};

const canDeletePost = (
  post,
  uid,
  currentTeamLeaderId
) => {
  if (!post || !uid || post.isOpen !== false) {
    return false;
  }

  if (post.type === "user") {
    return post.userId === uid;
  }

  if (post.type === "team") {
    return (
      !!currentTeamLeaderId &&
      !!post.teamId &&
      uid === currentTeamLeaderId
    );
  }

  return false;
};

const toggleOpen = async (
  post,
  nextState,
  showToast
) => {
  try {
    await updateDoc(
      lfgPostDoc(db, post.id),
      {
        isOpen: nextState,
        updatedAt: serverTimestamp(),
      }
    );
  } catch (error) {
    console.error(error);

    showToast?.(
      prettyFirestoreError(error),
      "error"
    );
  }
};

const reallyDeletePost = async (
  post,
  showError
) => {
  try {
    if (!auth.currentUser) {
      const error = new Error(
        "Please log in first."
      );
      error.code =
        "unauthenticated";
      throw error;
    }

    if (post.isOpen !== false) {
      return;
    }

    await deleteDoc(
      lfgPostDoc(db, post.id)
    );
  } catch (error) {
    showError?.(error);
    throw error;
  }
};

/* ======================================================================
   REACT SELECT
   ====================================================================== */

const MultiValue = (props) =>
  props.index < 3 ? (
    <components.MultiValue {...props} />
  ) : null;

const ValueContainer = (props) => {
  const { children, getValue } = props;

  const values = getValue() || [];
  const allChildren = Children.toArray(
    children[0]
  );

  const visible = allChildren.slice(0, 3);
  const hiddenCount = Math.max(
    0,
    values.length - visible.length
  );

  const hiddenLabels = values
    .slice(3)
    .map((value) => value.label);

  return (
    <components.ValueContainer {...props}>
      {visible}

      {hiddenCount > 0 && (
        <span
          className="--LFGV2-select-more"
          title={hiddenLabels.join(", ")}
        >
          +{hiddenCount}
        </span>
      )}

      {children[1]}
    </components.ValueContainer>
  );
};

/* ======================================================================
   SMALL UI COMPONENTS
   ====================================================================== */

function Avatar({
  name,
  size = "medium",
  tooltip = "",
}) {
  return (
    <span
      className={`--LFGV2-avatar --LFGV2-avatar--${size}`}
      {...(tooltip
        ? {
            "data-avatar-label": tooltip,
            title: tooltip,
            role: "img",
            "aria-label": tooltip,
          }
        : { "aria-hidden": "true" })}
    >
      {initialsFromName(name)}
    </span>
  );
}

function StatusBadge({ open }) {
  return (
    <span
      className={
        open
          ? "--LFGV2-status --LFGV2-status--visible"
          : "--LFGV2-status --LFGV2-status--hidden"
      }
    >
      {open ? "Visible" : "Hidden"}
    </span>
  );
}

function PostTypeBadge({ type }) {
  return (
    <span
      className={
        type === "team"
          ? "--LFGV2-post-type --LFGV2-post-type--team"
          : "--LFGV2-post-type --LFGV2-post-type--player"
      }
    >
      {type === "team" ? "TEAM" : "PLAYER"}
    </span>
  );
}

function MetadataBadge({
  children,
  variant = "default",
}) {
  return (
    <span
      className={`--LFGV2-meta-badge --LFGV2-meta-badge--${variant}`}
    >
      {children}
    </span>
  );
}

function RoleBadge({ role }) {
  return (
    <span
      className="--LFGV2-role-badge"
      title={role}
    >
      {role}
    </span>
  );
}

function EmptyState({
  title,
  description,
}) {
  return (
    <div className="--LFGV2-empty">
      <div className="--LFGV2-empty__icon">
        ◇
      </div>

      <h3 className="--LFGV2-empty__title">
        {title}
      </h3>

      {description && (
        <p className="--LFGV2-empty__description">
          {description}
        </p>
      )}
    </div>
  );
}

/* ======================================================================
   SIDEBAR
   ====================================================================== */

function Sidebar({
  activeSection,
  onNavigate,
  chatCount,
  savedCount,
}) {
  return (
    <aside className="--LFGV2-sidebar">
      <div className="--LFGV2-brand">
        <div className="--LFGV2-brand__mark">
          L
        </div>

        <div>
          <div className="--LFGV2-brand__name">
            LFG
          </div>

          <div className="--LFGV2-brand__sub">
            LOOKING FOR GROUP
          </div>
        </div>
      </div>

      <nav
        className="--LFGV2-sidebar__nav"
        aria-label="LFG navigation"
      >
        {NAV_ITEMS.map((item) => {
          const count =
            item.id === "messages"
              ? chatCount
              : item.id === "saved"
                ? savedCount
                : 0;

          return (
            <button
              key={item.id}
              type="button"
              className={
                activeSection === item.id
                  ? "--LFGV2-sidebar__nav-item --LFGV2-is-active"
                  : "--LFGV2-sidebar__nav-item"
              }
              onClick={() =>
                onNavigate(item.id)
              }
            >
              <span className="--LFGV2-sidebar__nav-icon">
                {item.icon}
              </span>

              <span className="--LFGV2-sidebar__nav-label">
                {item.label}
              </span>

              {count > 0 && (
                <span
                  className={
                    item.id === "saved"
                      ? "--LFGV2-sidebar__count --LFGV2-sidebar__count--muted"
                      : "--LFGV2-sidebar__count"
                  }
                >
                  {count}
                </span>
              )}
            </button>
          );
        })}
      </nav>
    </aside>
  );
}

/* ======================================================================
   FILTER BAR
   ====================================================================== */

function LFGFilters({
  filters,
  onChange,
  onToggleShowHiddenMine,
}) {
  const roleOptions = useMemo(
    () => toOptions(ROLES),
    []
  );

  const rankOptions = useMemo(
    () => toOptions(RANKS),
    []
  );

  const regionOptions = useMemo(
    () => toOptions(REGIONS),
    []
  );

  const typeOptions = useMemo(
    () => [
      {
        label: "Teams",
        value: "team",
      },
      {
        label: "Players",
        value: "user",
      },
    ],
    []
  );

  return (
    <div className="--LFGV2-filter-panel">
      <div className="--LFGV2-search">
        <span
          className="--LFGV2-search__icon"
          aria-hidden="true"
        >
          ⌕
        </span>

        <input
          className="--LFGV2-input --LFGV2-search__input"
          value={filters.q}
          placeholder="Search LFG posts..."
          onChange={(event) =>
            onChange({
              ...filters,
              q: event.target.value,
            })
          }
          onKeyDownCapture={
            stopSpacePropagation
          }
        />
      </div>

      <Select
        isMulti
        options={typeOptions}
        value={filters.types.map((value) => ({
          value,
          label:
            value === "team"
              ? "Teams"
              : "Players",
        }))}
        onChange={(values) =>
          onChange({
            ...filters,
            types: (values || []).map(
              (value) => value.value
            ),
          })
        }
        placeholder="Type"
        className="--LFGV2-filter-select --LFGV2-filter-select--type"
        classNamePrefix="--LFGV2-select"
        closeMenuOnSelect={false}
        hideSelectedOptions={false}
        components={{
          MultiValue,
          ValueContainer,
        }}
      />

      <Select
        isMulti
        options={regionOptions}
        value={filters.regions.map(
          (value) => ({
            value,
            label: value,
          })
        )}
        onChange={(values) =>
          onChange({
            ...filters,
            regions: (values || []).map(
              (value) => value.value
            ),
          })
        }
        placeholder="Region"
        className="--LFGV2-filter-select --LFGV2-filter-select--region"
        classNamePrefix="--LFGV2-select"
        closeMenuOnSelect={false}
        hideSelectedOptions={false}
        components={{
          MultiValue,
          ValueContainer,
        }}
      />

      <Select
        isMulti
        options={rankOptions}
        value={filters.ranks.map(
          (value) => ({
            value,
            label: value,
          })
        )}
        onChange={(values) =>
          onChange({
            ...filters,
            ranks: (values || []).map(
              (value) => value.value
            ),
          })
        }
        placeholder="Rank"
        className="--LFGV2-filter-select --LFGV2-filter-select--rank"
        classNamePrefix="--LFGV2-select"
        closeMenuOnSelect={false}
        hideSelectedOptions={false}
        components={{
          MultiValue,
          ValueContainer,
        }}
      />

      <Select
        isMulti
        options={roleOptions}
        value={filters.roles.map(
          (value) => ({
            value,
            label: value,
          })
        )}
        onChange={(values) =>
          onChange({
            ...filters,
            roles: (values || []).map(
              (value) => value.value
            ),
          })
        }
        placeholder="Roles"
        className="--LFGV2-filter-select --LFGV2-filter-select--roles"
        classNamePrefix="--LFGV2-select"
        closeMenuOnSelect={false}
        hideSelectedOptions={false}
        components={{
          MultiValue,
          ValueContainer,
        }}
      />

      <div className="--LFGV2-filter-actions">
        <button
          className="--LFGV2-btn --LFGV2-btn--secondary --LFGV2-filter-clear"
          type="button"
          onClick={() =>
            onChange({
              types: [],
              regions: [],
              ranks: [],
              roles: [],
              q: "",
              showHiddenMine:
                filters.showHiddenMine,
            })
          }
        >
          Clear Filters
        </button>

        <label className="--LFGV2-toggle-row">
          <input
            className="--LFGV2-toggle-row__native"
            type="checkbox"
            checked={filters.showHiddenMine}
            onChange={(event) =>
              onToggleShowHiddenMine(
                event.target.checked
              )
            }
          />

          <span className="--LFGV2-toggle">
            <span className="--LFGV2-toggle__thumb" />
          </span>

          <span className="--LFGV2-toggle-row__label">
            Show my hidden posts
          </span>
        </label>
      </div>
    </div>
  );
}

/* ======================================================================
   SOCIAL LINKS
   ====================================================================== */

function SocialLinks({
  socials = [],
}) {
  if (!socials.length) {
    return (
      <span className="--LFGV2-muted">
        No socials connected yet.
      </span>
    );
  }

  return (
    <div className="--LFGV2-social-list">
      {socials.map((social, index) => {
        const discordOnly =
          isDiscordLabel(social.label);

        const safeUrl =
          normalizeHttpUrl(
            social.url || ""
          );

        return (
          <div
            key={`${social.label}-${index}`}
            className="--LFGV2-social"
          >
            <span className="--LFGV2-social__label">
              {social.label || "Social"}
            </span>

            {discordOnly ? (
              <span className="--LFGV2-social__value">
                {social.url ||
                  "No Discord provided"}
              </span>
            ) : safeUrl ? (
              <a
                className="--LFGV2-social__link"
                href={safeUrl}
                target="_blank"
                rel="noopener noreferrer nofollow"
              >
                {social.url}
              </a>
            ) : (
              <span className="--LFGV2-social__invalid">
                Invalid or unsafe URL
              </span>
            )}
          </div>
        );
      })}
    </div>
  );
}

/* ======================================================================
   POST CARD
   ====================================================================== */

function LFGPostCard({
  post,
  viewerUid,
  currentTeamId,
  teamMemberUids,
  chattingAlready,
  menuOpen,
  saved,
  onToggleMenu,
  onReport,
  onToggleSaved,
  onConnect,
  onViewDetails,
  onEdit,
  onToggleOpen,
  onImportAvailability,
}) {
  const isTeam =
    post.type === "team";

  const teamMemberSet = useMemo(
    () => new Set(teamMemberUids),
    [teamMemberUids]
  );

  const canEdit =
    canManagePost(
      post,
      viewerUid,
      currentTeamId
    );

  const isOwnPlayerPost =
    post.type === "user" &&
    post.userId === viewerUid;

  const isOwnTeamPost =
    post.type === "team" &&
    !!currentTeamId &&
    post.teamId === currentTeamId;

  const alreadyMember =
    post.type === "user" &&
    !!currentTeamId &&
    teamMemberSet.has(post.userId);

  const topAgents =
    post.tierList?.[
      "My Top 3 Agents"
    ] || [];

  const teamQuestions =
    normalizeQuestionList(
      post.customQuestions
    );

  return (
    <article
      className={
        isTeam
          ? "--LFGV2-post-card --LFGV2-post-card--team"
          : "--LFGV2-post-card --LFGV2-post-card--player"
      }
      id={`lfg-${post.id}`}
    >
      <div className="--LFGV2-post-card__top">
        <div className="--LFGV2-post-card__identity">
          <Avatar
            name={postDisplayName(post)}
            size="large"
          />

          <div className="--LFGV2-post-card__identity-copy">
            <div className="--LFGV2-post-card__badge-row">
              <PostTypeBadge
                type={post.type}
              />

              {post.isOpen === false && (
                <StatusBadge open={false} />
              )}
            </div>

            <h3 className="--LFGV2-post-card__title">
              {postDisplayName(post)}
            </h3>

            <div className="--LFGV2-post-card__meta">
              <MetadataBadge>
                {postRegion(post) ||
                  "Region N/A"}
              </MetadataBadge>

              {isTeam && (
                <MetadataBadge>
                  {post.teamType ||
                    "Team type N/A"}
                </MetadataBadge>
              )}

              <MetadataBadge
                variant={
                  isTeam
                    ? "team-rank"
                    : "player-rank"
                }
              >
                {postRank(post) ||
                  "Rank N/A"}
                {isTeam ? " Avg." : ""}
              </MetadataBadge>

              {isTeam &&
                teamQuestions.length >
                  0 && (
                  <MetadataBadge variant="questionnaire">
                    {teamQuestions.length}{" "}
                    Question
                    {teamQuestions.length === 1
                      ? ""
                      : "s"}
                  </MetadataBadge>
                )}

              {!isTeam &&
                post.peakRank && (
                  <MetadataBadge variant="peak">
                    Peak {post.peakRank}
                  </MetadataBadge>
                )}
            </div>
          </div>
        </div>

        <div className="--LFGV2-post-card__top-actions">
          <button
            type="button"
            className={
              saved
                ? "--LFGV2-icon-btn --LFGV2-icon-btn--saved"
                : "--LFGV2-icon-btn"
            }
            onClick={() =>
              onToggleSaved(post.id)
            }
            aria-label={
              saved
                ? "Remove saved post"
                : "Save post"
            }
            title={
              saved
                ? "Remove saved post"
                : "Save post"
            }
          >
            {saved ? "★" : "☆"}
          </button>

          <div className="--LFGV2-post-menu-wrap">
            <button
              type="button"
              className="--LFGV2-icon-btn"
              onClick={(event) => {
                event.stopPropagation();
                onToggleMenu(
                  menuOpen
                    ? null
                    : post.id
                );
              }}
              aria-label="Post options"
            >
              ⋯
            </button>

            {menuOpen && (
              <div className="--LFGV2-post-menu">
                {canEdit && (
                  <button
                    type="button"
                    className="--LFGV2-post-menu__item"
                    onClick={() => {
                      onToggleMenu(null);
                      onEdit(post);
                    }}
                  >
                    Edit post
                  </button>
                )}

                {canEdit && (
                  <button
                    type="button"
                    className="--LFGV2-post-menu__item"
                    onClick={() => {
                      onToggleMenu(null);
                      onToggleOpen(
                        post,
                        post.isOpen ===
                          false
                      );
                    }}
                  >
                    {post.isOpen !== false
                      ? "Hide post"
                      : "Unhide post"}
                  </button>
                )}

                <button
                  type="button"
                  className="--LFGV2-post-menu__item --LFGV2-post-menu__item--danger"
                  onClick={() => {
                    onToggleMenu(null);
                    onReport(post);
                  }}
                >
                  Report post
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {isTeam &&
        post.lookingForRoles?.length >
          0 && (
          <div className="--LFGV2-post-card__section">
            <span className="--LFGV2-eyebrow">
              Looking For
            </span>

            <div className="--LFGV2-role-list">
              {post.lookingForRoles.map(
                (role) => (
                  <RoleBadge
                    key={role}
                    role={role}
                  />
                )
              )}
            </div>
          </div>
        )}

      {!isTeam &&
        topAgents.length > 0 && (
          <div className="--LFGV2-post-card__section">
            <span className="--LFGV2-eyebrow">
              Top Agents
            </span>

            <div className="--LFGV2-agent-preview">
              {topAgents.map((agent) => (
                <span
                  key={agent}
                  className="--LFGV2-agent-token"
                  title={agent}
                >
                  {initialsFromName(agent)}
                </span>
              ))}
            </div>
          </div>
        )}

      <p className="--LFGV2-post-card__description">
        {isTeam
          ? post.teamDescription?.trim() ||
            "No team description yet."
          : post.description?.trim() ||
            "No player description yet."}
      </p>

      {isTeam &&
        post.teamMembers?.length >
          0 && (
          <div className="--LFGV2-post-card__member-row">
            <div>
              <span className="--LFGV2-eyebrow">
                Members
              </span>

              <div className="--LFGV2-avatar-stack">
                {post.teamMembers
                  .filter(
                    (member) =>
                      (
                        member.role || ""
                      ).toLowerCase() !==
                      "spectator"
                  )
                  .slice(0, 6)
                  .map((member) => (
                    <Avatar
                      key={member.uid}
                      name={member.username}
                      size="small"
                      tooltip={
                        member.username ||
                        "Unknown"
                      }
                    />
                  ))}

                {post.teamMembers.length >
                  6 && (
                  <span className="--LFGV2-avatar --LFGV2-avatar--small --LFGV2-avatar--count">
                    +
                    {post.teamMembers
                      .length - 6}
                  </span>
                )}
              </div>
            </div>
          </div>
        )}

      <div className="--LFGV2-post-card__actions">
        <button
          type="button"
          className="--LFGV2-btn --LFGV2-btn--secondary --LFGV2-btn--grow"
          onClick={() =>
            onViewDetails(post)
          }
        >
          View Details
        </button>

        {!isOwnPlayerPost &&
          !isOwnTeamPost &&
          (alreadyMember ? (
            <span className="--LFGV2-static-action">
              Already on your team
            </span>
          ) : chattingAlready ? (
            <button
              type="button"
              disabled
              className="--LFGV2-btn --LFGV2-btn--success --LFGV2-btn--grow"
            >
              Chatting Already
            </button>
          ) : (
            <button
              type="button"
              className="--LFGV2-btn --LFGV2-btn--primary --LFGV2-btn--grow --LFGV2-btn--connect"
              onClick={() =>
                onConnect(post)
              }
            >
              Connect
            </button>
          ))}

        {post.type === "user" &&
          currentTeamId &&
          post.availabilityShared && (
            <button
              type="button"
              className="--LFGV2-btn --LFGV2-btn--availability"
              onClick={() =>
                onImportAvailability(
                  post
                )
              }
            >
              Import Availability
            </button>
          )}
      </div>
    </article>
  );
}

/* ======================================================================
   POST DETAIL
   ====================================================================== */

function PostDetailView({
  post,
  viewerUid,
  currentTeamId,
  teamMemberUids,
  chattingAlready,
  saved,
  onBack,
  onReport,
  onToggleSaved,
  onConnect,
  onEdit,
  onImportAvailability,
  showBackButton = true,
}) {
  const isTeam =
    post.type === "team";

  const topAgents =
    post.tierList?.[
      "My Top 3 Agents"
    ] || [];

  const teamQuestions =
    normalizeQuestionList(
      post.customQuestions
    );

  const teamMemberSet =
    new Set(teamMemberUids);

  const ownPlayer =
    post.type === "user" &&
    post.userId === viewerUid;

  const ownTeam =
    post.type === "team" &&
    currentTeamId === post.teamId;

  const alreadyMember =
    post.type === "user" &&
    !!currentTeamId &&
    teamMemberSet.has(post.userId);

  const editable =
    canManagePost(
      post,
      viewerUid,
      currentTeamId
    );

  return (
    <div className="--LFGV2-subpage">
      <div
        className={
          showBackButton
            ? "--LFGV2-subpage__topbar"
            : "--LFGV2-subpage__topbar --LFGV2-subpage__topbar--actions-only"
        }
      >
        <button
          type="button"
          className="--LFGV2-back-btn"
          onClick={onBack}
        >
          ← Back to Feed
        </button>

        <div className="--LFGV2-subpage__top-actions">
          <button
            type="button"
            className={
              saved
                ? "--LFGV2-icon-btn --LFGV2-icon-btn--saved"
                : "--LFGV2-icon-btn"
            }
            onClick={() =>
              onToggleSaved(post.id)
            }
          >
            {saved ? "★" : "☆"}
          </button>

          <button
            type="button"
            className="--LFGV2-btn --LFGV2-btn--danger-soft"
            onClick={() =>
              onReport(post)
            }
          >
            Report
          </button>
        </div>
      </div>

      <section className="--LFGV2-detail-card">
        <div className="--LFGV2-detail-card__hero">
          <Avatar
            name={postDisplayName(post)}
            size="hero"
          />

          <div className="--LFGV2-detail-card__hero-copy">
            <div className="--LFGV2-detail-card__label">
              <PostTypeBadge
                type={post.type}
              />
            </div>

            <h1 className="--LFGV2-detail-card__title">
              {postDisplayName(post)}
            </h1>

            <div className="--LFGV2-detail-card__badges">
              <MetadataBadge>
                {postRegion(post) ||
                  "N/A"}
              </MetadataBadge>

              {isTeam && (
                <MetadataBadge>
                  {post.teamType ||
                    "N/A"}
                </MetadataBadge>
              )}

              <MetadataBadge>
                {postRank(post) ||
                  "N/A"}
                {isTeam ? " Avg." : ""}
              </MetadataBadge>

              {isTeam &&
                teamQuestions.length >
                  0 && (
                  <MetadataBadge variant="questionnaire">
                    {teamQuestions.length}{" "}
                    Question
                    {teamQuestions.length ===
                    1
                      ? ""
                      : "s"}
                  </MetadataBadge>
                )}

              {!isTeam &&
                post.peakRank && (
                  <MetadataBadge variant="peak">
                    Peak {post.peakRank}
                  </MetadataBadge>
                )}
            </div>
          </div>
        </div>

        <p className="--LFGV2-detail-card__description">
          {isTeam
            ? post.teamDescription?.trim() ||
              "No team description yet."
            : post.description?.trim() ||
              "No player description yet."}
        </p>

        {isTeam ? (
          <>
            <section className="--LFGV2-detail-section">
              <h2 className="--LFGV2-detail-section__title">
                Looking For
              </h2>

              <div className="--LFGV2-role-list">
                {post.lookingForRoles?.length ? (
                  post.lookingForRoles.map(
                    (role) => (
                      <RoleBadge
                        key={role}
                        role={role}
                      />
                    )
                  )
                ) : (
                  <span className="--LFGV2-muted">
                    No specific roles listed.
                  </span>
                )}
              </div>
            </section>

            <section className="--LFGV2-detail-grid">
              <div className="--LFGV2-stat-card">
                <span>Team Type</span>
                <strong>
                  {post.teamType ||
                    "N/A"}
                </strong>
              </div>

              <div className="--LFGV2-stat-card">
                <span>Region</span>
                <strong>
                  {post.teamRegion ||
                    "N/A"}
                </strong>
              </div>

              <div className="--LFGV2-stat-card">
                <span>Average Rank</span>
                <strong>
                  {post.currentAvgRank ||
                    "N/A"}
                </strong>
              </div>
            </section>

            <section className="--LFGV2-detail-section">
              <h2 className="--LFGV2-detail-section__title">
                Team Socials
              </h2>

              <SocialLinks
                socials={
                  post.teamSocials || []
                }
              />
            </section>

            <section className="--LFGV2-detail-section">
              <h2 className="--LFGV2-detail-section__title">
                Team Roster
              </h2>

              <div className="--LFGV2-roster-list">
                {(post.teamMembers || [])
                  .filter(
                    (member) =>
                      (
                        member.role || ""
                      ).toLowerCase() !==
                      "spectator"
                  )
                  .map((member) => (
                    <div
                      key={member.uid}
                      className="--LFGV2-roster-row"
                    >
                      <div className="--LFGV2-roster-row__user">
                        <Avatar
                          name={
                            member.username
                          }
                          size="small"
                          tooltip={
                            member.username ||
                            "Unknown"
                          }
                        />

                        <span>
                          {member.username ||
                            "Unknown"}
                        </span>
                      </div>

                      <RoleBadge
                        role={
                          member.role ||
                          "Player"
                        }
                      />
                    </div>
                  ))}

                {(!post.teamMembers ||
                  post.teamMembers.length ===
                    0) && (
                  <span className="--LFGV2-muted">
                    No roster synced yet.
                  </span>
                )}
              </div>
            </section>

            {teamQuestions.length > 0 && (
              <section className="--LFGV2-detail-section">
                <h2 className="--LFGV2-detail-section__title">
                  Application Questions
                </h2>

                <div className="--LFGV2-question-list">
                  {teamQuestions.map(
                    (question, index) => (
                      <div
                        key={`${post.id}-question-${index}`}
                        className="--LFGV2-question-chip"
                      >
                        <strong>
                          Question {index + 1}
                        </strong>

                        <span>
                          {question}
                        </span>
                      </div>
                    )
                  )}
                </div>
              </section>
            )}
          </>
        ) : (
          <>
            {(post.qStrength ||
              post.qWorkOn ||
              post.qCurrently) && (
              <section className="--LFGV2-detail-section">
                <h2 className="--LFGV2-detail-section__title">
                  Q&A
                </h2>

                <div className="--LFGV2-qa-grid">
                  <div className="--LFGV2-qa-card">
                    <span>
                      Biggest Strength
                    </span>

                    <strong>
                      {post.qStrength ||
                        "—"}
                    </strong>
                  </div>

                  <div className="--LFGV2-qa-card">
                    <span>
                      Needs Work
                    </span>

                    <strong>
                      {post.qWorkOn ||
                        "—"}
                    </strong>
                  </div>

                  <div className="--LFGV2-qa-card">
                    <span>
                      Currently Working On
                    </span>

                    <strong>
                      {post.qCurrently ||
                        "—"}
                    </strong>
                  </div>
                </div>
              </section>
            )}

            {post.tierList && (
              <section className="--LFGV2-detail-section">
                <h2 className="--LFGV2-detail-section__title">
                  Agent Tier List
                </h2>

                <div className="--LFGV2-tier-display">
                  {TIER_TITLES.map(
                    (tierTitle) => (
                      <div
                        key={tierTitle}
                        className="--LFGV2-tier-display__row"
                      >
                        <span className="--LFGV2-tier-display__label">
                          {tierTitle}
                        </span>

                        <div className="--LFGV2-agent-preview">
                          {(
                            post.tierList[
                              tierTitle
                            ] || []
                          ).length ? (
                            post.tierList[
                              tierTitle
                            ].map(
                              (agent) => (
                                <span
                                  key={
                                    agent
                                  }
                                  className="--LFGV2-agent-token --LFGV2-agent-token--named"
                                >
                                  {agent}
                                </span>
                              )
                            )
                          ) : (
                            <span className="--LFGV2-muted">
                              —
                            </span>
                          )}
                        </div>
                      </div>
                    )
                  )}
                </div>
              </section>
            )}

            {topAgents.length > 0 && (
              <section className="--LFGV2-detail-section">
                <h2 className="--LFGV2-detail-section__title">
                  Top Agents
                </h2>

                <div className="--LFGV2-agent-preview --LFGV2-agent-preview--large">
                  {topAgents.map(
                    (agent) => (
                      <span
                        key={agent}
                        className="--LFGV2-agent-token --LFGV2-agent-token--named"
                      >
                        {agent}
                      </span>
                    )
                  )}
                </div>
              </section>
            )}

            {post.availabilityShared && (
              <div className="--LFGV2-availability-banner">
                <div className="--LFGV2-availability-banner__icon">
                  ✓
                </div>

                <div>
                  <strong>
                    Weekly Availability
                    Shared
                  </strong>

                  <span>
                    Teams can import a
                    read-only copy.
                  </span>
                </div>
              </div>
            )}

            <section className="--LFGV2-detail-section">
              <h2 className="--LFGV2-detail-section__title">
                Socials
              </h2>

              <SocialLinks
                socials={post.socials || []}
              />
            </section>
          </>
        )}

        <div className="--LFGV2-detail-card__footer">
          {!ownPlayer &&
            !ownTeam &&
            !alreadyMember &&
            (chattingAlready ? (
              <button
                type="button"
                className="--LFGV2-btn --LFGV2-btn--success"
                disabled
              >
                Chatting Already
              </button>
            ) : (
              <button
                type="button"
                className="--LFGV2-btn --LFGV2-btn--primary --LFGV2-btn--connect"
                onClick={() =>
                  onConnect(post)
                }
              >
                Connect
              </button>
            ))}

          {!ownPlayer &&
            !ownTeam &&
            alreadyMember && (
              <span className="--LFGV2-static-action">
                Already on your team
              </span>
            )}

          {post.type === "user" &&
            currentTeamId &&
            post.availabilityShared && (
              <button
                type="button"
                className="--LFGV2-btn --LFGV2-btn--secondary"
                onClick={() =>
                  onImportAvailability(
                    post
                  )
                }
              >
                Import Availability
              </button>
            )}

          {editable && (
            <button
              type="button"
              className="--LFGV2-btn --LFGV2-btn--secondary"
              onClick={() =>
                onEdit(post)
              }
            >
              Edit Post
            </button>
          )}
        </div>
      </section>
    </div>
  );
}

/* ======================================================================
   AGENT TIER BUILDER
   ====================================================================== */

function AgentTierBuilder({
  tiers,
  setTiers,
  unassigned,
  setUnassigned,
}) {
  return (
    <div className="--LFGV2-tier-builder">
      <div className="--LFGV2-tier-builder__main">
        {TIER_TITLES.map(
          (tierTitle, index) => (
            <div
              key={tierTitle}
              className={`--LFGV2-tier-row --LFGV2-tier-row--${index + 1}`}
            >
              <div className="--LFGV2-tier-row__label">
                <strong>
                  {tierTitle}
                </strong>

                {tierTitle ===
                  "My Top 3 Agents" && (
                  <span>
                    Max 3
                  </span>
                )}
              </div>

              <ReactSortable
                className="--LFGV2-tier-row__drop"
                list={(
                  tiers[tierTitle] || []
                ).map((agent) => ({
                  id: agent,
                  name: agent,
                }))}
                setList={(next) => {
                  const incoming =
                    next.map(
                      (item) => item.name
                    );

                  setTiers((previous) => {
                    const nextTiers = {
                      ...previous,
                      [tierTitle]:
                        tierTitle ===
                        "My Top 3 Agents"
                          ? incoming.slice(
                              0,
                              3
                            )
                          : incoming,
                    };

                    const cleaned =
                      sanitizeTiers(
                        nextTiers
                      );

                    setUnassigned(
                      computeUnassignedFromTiers(
                        cleaned
                      )
                    );

                    return cleaned;
                  });
                }}
                group="agents"
                animation={150}
                ghostClass="--LFGV2-is-dragging"
              >
                {(
                  tiers[tierTitle] || []
                ).map((agent) => (
                  <button
                    key={agent}
                    type="button"
                    className="--LFGV2-tier-agent"
                  >
                    <span className="--LFGV2-tier-agent__avatar">
                      {initialsFromName(
                        agent
                      )}
                    </span>

                    <span>
                      {agent}
                    </span>
                  </button>
                ))}
              </ReactSortable>
            </div>
          )
        )}
      </div>

      <div className="--LFGV2-tier-unassigned">
        <div className="--LFGV2-tier-unassigned__head">
          <strong>
            Unassigned
          </strong>

          <span className="--LFGV2-count-badge">
            {unassigned.length}
          </span>
        </div>

        <ReactSortable
          className="--LFGV2-tier-unassigned__grid"
          list={unassigned.map(
            (agent) => ({
              id: agent,
              name: agent,
            })
          )}
          setList={(next) => {
            const nextUnassigned =
              next.map(
                (item) => item.name
              );

            setUnassigned(
              nextUnassigned
            );

            setTiers((previous) => {
              const cleaned = {};
              const unassignedSet =
                new Set(
                  nextUnassigned
                );

              TIER_TITLES.forEach(
                (title) => {
                  cleaned[title] = (
                    previous[title] ||
                    []
                  ).filter(
                    (agent) =>
                      !unassignedSet.has(
                        agent
                      )
                  );
                }
              );

              cleaned[
                "My Top 3 Agents"
              ] = (
                cleaned[
                  "My Top 3 Agents"
                ] || []
              ).slice(0, 3);

              return cleaned;
            });
          }}
          group="agents"
          animation={150}
          ghostClass="--LFGV2-is-dragging"
        >
          {unassigned.map(
            (agent) => (
              <button
                key={agent}
                type="button"
                className="--LFGV2-unassigned-agent"
                title={agent}
              >
                <span>
                  {initialsFromName(
                    agent
                  )}
                </span>

                <small>
                  {agent}
                </small>
              </button>
            )
          )}
        </ReactSortable>
      </div>
    </div>
  );
}

/* ======================================================================
   WIZARD
   ====================================================================== */

function WizardSidebar({
  title,
  steps,
  activeStep,
  onStepSelect,
}) {
  return (
    <aside className="--LFGV2-wizard-sidebar">
      <span className="--LFGV2-wizard-sidebar__eyebrow">
        {title}
      </span>

      <div className="--LFGV2-wizard-sidebar__steps">
        {steps.map(
          (step, index) => (
            <button
              key={step}
              type="button"
              className={
                index === activeStep
                  ? "--LFGV2-wizard-step --LFGV2-wizard-step--active"
                  : index < activeStep
                    ? "--LFGV2-wizard-step --LFGV2-wizard-step--complete"
                    : "--LFGV2-wizard-step"
              }
              aria-current={
                index === activeStep
                  ? "step"
                  : undefined
              }
              onClick={() =>
                onStepSelect?.(index)
              }
            >
              <span className="--LFGV2-wizard-step__number">
                {index < activeStep
                  ? "✓"
                  : index + 1}
              </span>

              <span>
                {step}
              </span>
            </button>
          )
        )}
      </div>
    </aside>
  );
}

function WizardFooter({
  step,
  lastStep,
  onBack,
  onNext,
  onCancel,
  onSave,
  saveLabel,
}) {
  return (
    <div className="--LFGV2-wizard-footer">
      <div>
        {step === 0 ? (
          <button
            type="button"
            className="--LFGV2-btn --LFGV2-btn--secondary app-cancel-control"
            onClick={onCancel}
          >
            Cancel
          </button>
        ) : (
          <button
            type="button"
            className="--LFGV2-btn --LFGV2-btn--secondary"
            onClick={onBack}
          >
            ← Back
          </button>
        )}
      </div>

      <div>
        {step < lastStep ? (
          <button
            type="button"
            className="--LFGV2-btn --LFGV2-btn--primary"
            onClick={onNext}
          >
            Next →
          </button>
        ) : (
          <button
            type="button"
            className="--LFGV2-btn --LFGV2-btn--primary"
            onClick={onSave}
          >
            {saveLabel}
          </button>
        )}
      </div>
    </div>
  );
}

/* ======================================================================
   PLAYER POST EDITOR
   ====================================================================== */

function PlayerPostEditor({
  existingPost = null,
  onCancel,
  onSaved,
  showToast,
  showError,
}) {
  const steps = [
    "Basic Info",
    "Q&A",
    "Agent Tier List",
    "Availability",
    "Review",
  ];

  const [step, setStep] =
    useState(0);

  const [profileName, setProfileName] =
    useState("");

  const [
    shareAvailability,
    setShareAvailability,
  ] = useState(false);

  const [form, setForm] =
    useState({
      socials: [
        {
          label: "",
          url: "",
        },
      ],
      description: "",
      qStrength: "",
      qWorkOn: "",
      qCurrently: "",
      userRegion: "NA-E",
      currentRank: "Diamond I",
      peakRank: "Immortal I",
      hideFromMyTeams: false,
    });

  const [tiers, setTiers] =
    useState(blankTiers);

  const [
    unassigned,
    setUnassigned,
  ] = useState(AGENTS);

  useEffect(() => {
    const loadProfile = async () => {
      if (!auth.currentUser) {
        return;
      }

      try {
        const snapshot =
          await getDoc(
            doc(
              db,
              "users",
              auth.currentUser.uid
            )
          );

        if (snapshot.exists()) {
          setProfileName(
            snapshot.data()
              .username || ""
          );
        }
      } catch (_) {
        // Display-only profile fetch.
      }
    };

    loadProfile();
  }, []);

  useEffect(() => {
    if (!existingPost) {
      return;
    }

    setProfileName(
      existingPost.username || ""
    );

    setForm({
      socials:
        Array.isArray(
          existingPost.socials
        ) &&
        existingPost.socials.length
          ? existingPost.socials
          : [
              {
                label: "",
                url: "",
              },
            ],

      description:
        existingPost.description || "",

      qStrength:
        existingPost.qStrength || "",

      qWorkOn:
        existingPost.qWorkOn || "",

      qCurrently:
        existingPost.qCurrently || "",

      userRegion:
        existingPost.userRegion ||
        "NA-E",

      currentRank:
        existingPost.currentRank ||
        "Diamond I",

      peakRank:
        existingPost.peakRank ||
        "Immortal I",

      hideFromMyTeams:
        !!existingPost.hideFromMyTeams,
    });

    setShareAvailability(
      !!existingPost.availabilityShared
    );

    if (existingPost.tierList) {
      const cleaned =
        sanitizeTiers(
          existingPost.tierList
        );

      setTiers(cleaned);

      setUnassigned(
        computeUnassignedFromTiers(
          cleaned
        )
      );
    } else {
      setTiers(blankTiers());
      setUnassigned(AGENTS);
    }
  }, [existingPost]);

  const updateSocial = (
    index,
    key,
    value
  ) => {
    setForm((previous) => {
      const next = [
        ...previous.socials,
      ];

      next[index] = {
        ...next[index],
        [key]: value,
      };

      return {
        ...previous,
        socials: next,
      };
    });
  };

  const addSocial = () => {
    setForm((previous) => {
      if (
        previous.socials.length >= 4
      ) {
        return previous;
      }

      return {
        ...previous,
        socials: [
          ...previous.socials,
          {
            label: "",
            url: "",
          },
        ],
      };
    });
  };

  const removeSocial = (index) => {
    setForm((previous) => ({
      ...previous,
      socials:
        previous.socials.filter(
          (_, itemIndex) =>
            itemIndex !== index
        ),
    }));
  };

  const submit = async () => {
    if (!auth.currentUser) {
      showToast?.(
        "Please log in first.",
        "warning"
      );

      return;
    }

    try {
      const userUid =
        auth.currentUser.uid;

      const profileSnapshot =
        await getDoc(
          doc(db, "users", userUid)
        );

      const profile =
        profileSnapshot.exists()
          ? profileSnapshot.data()
          : {};

      const username =
        existingPost?.username ||
        profile.username ||
        profileName ||
        "Unknown";

      const hiddenFromTeamIds =
        form.hideFromMyTeams
          ? gatherUserTeamIds(
              profile,
              profile.activeTeamId ||
                profile.teamId ||
                null
            )
          : [];

      const timezone =
        localStorage.getItem(
          "calendar.tz"
        ) ||
        Intl.DateTimeFormat()
          .resolvedOptions().timeZone ||
        "UTC";

      const personalAvailabilities =
        Array.isArray(
          profile.personalAvailabilities
        )
          ? profile.personalAvailabilities
          : [];

      const {
        setAvailabilityShare,
      } = await import(
        "../../components/Calendar/_availabilityShared"
      );

      await setAvailabilityShare({
        userId: userUid,
        share:
          !!shareAvailability,
        tz: timezone,
        personalAvailabilities,
      });

      const payload = {
        type: "user",

        userId:
          existingPost?.userId ||
          userUid,

        username,

        socials: form.socials
          .filter(
            (social) =>
              social.label &&
              social.url
          )
          .slice(0, 4),

        description: clampWords(
          form.description,
          120
        ),

        qStrength: clampWords(
          form.qStrength,
          75
        ),

        qWorkOn: clampWords(
          form.qWorkOn,
          75
        ),

        qCurrently: clampWords(
          form.qCurrently,
          75
        ),

        tierList:
          sanitizeTiers(tiers),

        userRegion:
          form.userRegion,

        currentRank:
          form.currentRank,

        peakRank:
          form.peakRank,

        hideFromMyTeams:
          !!form.hideFromMyTeams,

        hiddenFromTeamIds,

        availabilityShared:
          !!shareAvailability,

        updatedAt:
          serverTimestamp(),

        isOpen:
          existingPost?.isOpen ??
          true,
      };

      if (existingPost?.id) {
        await updateDoc(
          lfgPostDoc(
            db,
            existingPost.id
          ),
          payload
        );
      } else {
        await setDoc(
          doc(lfgPostsCollection(db)),
          {
            ...payload,

            createdBy: userUid,

            createdAt:
              serverTimestamp(),
          }
        );
      }

      showToast?.(
        existingPost
          ? "Player LFG updated."
          : "Player LFG created.",
        "success"
      );

      onSaved?.();
    } catch (error) {
      showError?.(error);
    }
  };

  return (
    <div className="--LFGV2-editor-page">
      <div className="--LFGV2-editor-page__heading">
        <div>
          <span className="--LFGV2-page-kicker">
            Player Recruitment Profile
          </span>

          <h1 className="--LFGV2-page-title">
            {existingPost
              ? "Edit Player Post"
              : "Create Player Post"}
          </h1>

          <p className="--LFGV2-page-subtitle">
            Build a profile teams can
            quickly understand.
          </p>
        </div>
      </div>

      <div className="--LFGV2-wizard-layout">
        <WizardSidebar
          title="Player Post"
          steps={steps}
          activeStep={step}
          onStepSelect={setStep}
        />

        <div className="--LFGV2-wizard-content">
          {step === 0 && (
            <div className="--LFGV2-form-section">
              <div className="--LFGV2-form-section__heading">
                <h2>
                  Basic Information
                </h2>

                <p>
                  Your core player
                  details and contact
                  information.
                </p>
              </div>

              <div className="--LFGV2-form-grid --LFGV2-form-grid--three">
                <label className="--LFGV2-field">
                  <span>
                    Display Name
                  </span>

                  <input
                    className="--LFGV2-input"
                    value={
                      profileName ||
                      existingPost?.username ||
                      ""
                    }
                    readOnly
                    placeholder="Profile username"
                  />
                </label>

                <label className="--LFGV2-field">
                  <span>
                    Server Region
                  </span>

                  <select
                    className="--LFGV2-input"
                    value={
                      form.userRegion
                    }
                    onChange={(event) =>
                      setForm({
                        ...form,
                        userRegion:
                          event.target
                            .value,
                      })
                    }
                  >
                    {REGIONS.map(
                      (region) => (
                        <option
                          key={region}
                          value={region}
                        >
                          {region}
                        </option>
                      )
                    )}
                  </select>
                </label>

                <label className="--LFGV2-field">
                  <span>
                    Current Rank
                  </span>

                  <select
                    className="--LFGV2-input"
                    value={
                      form.currentRank
                    }
                    onChange={(event) =>
                      setForm({
                        ...form,
                        currentRank:
                          event.target
                            .value,
                      })
                    }
                  >
                    {RANKS.map(
                      (rank) => (
                        <option
                          key={rank}
                          value={rank}
                        >
                          {rank}
                        </option>
                      )
                    )}
                  </select>
                </label>

                <label className="--LFGV2-field">
                  <span>
                    Peak Rank
                  </span>

                  <select
                    className="--LFGV2-input"
                    value={
                      form.peakRank
                    }
                    onChange={(event) =>
                      setForm({
                        ...form,
                        peakRank:
                          event.target
                            .value,
                      })
                    }
                  >
                    {RANKS.map(
                      (rank) => (
                        <option
                          key={rank}
                          value={rank}
                        >
                          {rank}
                        </option>
                      )
                    )}
                  </select>
                </label>
              </div>

              <label className="--LFGV2-field">
                <span>
                  Description
                  <small>
                    Maximum 120 words
                  </small>
                </span>

                <textarea
                  className="--LFGV2-input --LFGV2-textarea"
                  value={
                    form.description
                  }
                  onChange={(event) =>
                    setForm({
                      ...form,
                      description:
                        clampWords(
                          event.target
                            .value,
                          120
                        ),
                    })
                  }
                  onKeyDownCapture={
                    stopSpacePropagation
                  }
                  placeholder="Tell teams what kind of player you are, what you want to achieve, and what you're looking for."
                />
              </label>

              <div className="--LFGV2-form-subsection">
                <div className="--LFGV2-form-subsection__head">
                  <div>
                    <h3>
                      Socials
                    </h3>

                    <p>
                      Add up to four
                      contact methods.
                    </p>
                  </div>

                  <button
                    type="button"
                    className="--LFGV2-btn --LFGV2-btn--secondary"
                    onClick={addSocial}
                    disabled={
                      form.socials
                        .length >= 4
                    }
                  >
                    + Add Social
                  </button>
                </div>

                <div className="--LFGV2-social-editor">
                  {form.socials.map(
                    (
                      social,
                      index
                    ) => (
                      <div
                        key={index}
                        className="--LFGV2-social-editor__row"
                      >
                        <input
                          className="--LFGV2-input"
                          placeholder="Discord, Twitter, Twitch..."
                          value={
                            social.label
                          }
                          onChange={(
                            event
                          ) =>
                            updateSocial(
                              index,
                              "label",
                              event.target
                                .value
                            )
                          }
                          onKeyDownCapture={
                            stopSpacePropagation
                          }
                        />

                        <input
                          className="--LFGV2-input"
                          placeholder="Username or https://..."
                          value={
                            social.url
                          }
                          onChange={(
                            event
                          ) =>
                            updateSocial(
                              index,
                              "url",
                              event.target
                                .value
                            )
                          }
                          onKeyDownCapture={
                            stopSpacePropagation
                          }
                        />

                        <button
                          type="button"
                          className="--LFGV2-icon-btn --LFGV2-icon-btn--danger app-cancel-control"
                          onClick={() =>
                            removeSocial(
                              index
                            )
                          }
                          aria-label="Remove social"
                        >
                          ×
                        </button>
                      </div>
                    )
                  )}
                </div>
              </div>
            </div>
          )}

          {step === 1 && (
            <div className="--LFGV2-form-section">
              <div className="--LFGV2-form-section__heading">
                <h2>
                  Player Q&A
                </h2>

                <p>
                  Give teams more
                  context about how you
                  play and what you want
                  to improve.
                </p>
              </div>

              <label className="--LFGV2-field">
                <span>
                  What's your biggest
                  strength?
                  <small>
                    Maximum 75 words
                  </small>
                </span>

                <textarea
                  className="--LFGV2-input --LFGV2-textarea"
                  value={
                    form.qStrength
                  }
                  onChange={(event) =>
                    setForm({
                      ...form,
                      qStrength:
                        clampWords(
                          event.target
                            .value,
                          75
                        ),
                    })
                  }
                  onKeyDownCapture={
                    stopSpacePropagation
                  }
                />
              </label>

              <label className="--LFGV2-field">
                <span>
                  What do you need to
                  work on?
                  <small>
                    Maximum 75 words
                  </small>
                </span>

                <textarea
                  className="--LFGV2-input --LFGV2-textarea"
                  value={form.qWorkOn}
                  onChange={(event) =>
                    setForm({
                      ...form,
                      qWorkOn:
                        clampWords(
                          event.target
                            .value,
                          75
                        ),
                    })
                  }
                  onKeyDownCapture={
                    stopSpacePropagation
                  }
                />
              </label>

              <label className="--LFGV2-field">
                <span>
                  What are you currently
                  working on?
                  <small>
                    Maximum 75 words
                  </small>
                </span>

                <textarea
                  className="--LFGV2-input --LFGV2-textarea"
                  value={
                    form.qCurrently
                  }
                  onChange={(event) =>
                    setForm({
                      ...form,
                      qCurrently:
                        clampWords(
                          event.target
                            .value,
                          75
                        ),
                    })
                  }
                  onKeyDownCapture={
                    stopSpacePropagation
                  }
                />
              </label>
            </div>
          )}

          {step === 2 && (
            <div className="--LFGV2-form-section">
              <div className="--LFGV2-form-section__heading">
                <h2>
                  Agent Tier List
                </h2>

                <p>
                  Drag agents between
                  categories. Each agent
                  can only appear once.
                </p>
              </div>

              <AgentTierBuilder
                tiers={tiers}
                setTiers={setTiers}
                unassigned={
                  unassigned
                }
                setUnassigned={
                  setUnassigned
                }
              />
            </div>
          )}

          {step === 3 && (
            <div className="--LFGV2-form-section">
              <div className="--LFGV2-form-section__heading">
                <h2>
                  Share Your Weekly
                  Availability
                </h2>

                <p>
                  Teams can import a
                  read-only copy of your
                  availability directly
                  into their calendar.
                </p>
              </div>

              <div className="--LFGV2-share-card">
                <div className="--LFGV2-share-card__icon">
                  ◫
                </div>

                <div className="--LFGV2-share-card__copy">
                  <strong>
                    Attach my weekly
                    availability
                  </strong>

                  <span>
                    You can turn this
                    off again whenever
                    you edit the post.
                  </span>
                </div>

                <label className="--LFGV2-toggle-row --LFGV2-toggle-row--compact">
                  <input
                    className="--LFGV2-toggle-row__native"
                    type="checkbox"
                    checked={
                      shareAvailability
                    }
                    onChange={(event) =>
                      setShareAvailability(
                        event.target
                          .checked
                      )
                    }
                  />

                  <span className="--LFGV2-toggle">
                    <span className="--LFGV2-toggle__thumb" />
                  </span>
                </label>
              </div>

              <div className="--LFGV2-info-card">
                <strong>
                  How this works
                </strong>

                <ul>
                  <li>
                    Your personal
                    availability remains
                    the source.
                  </li>

                  <li>
                    Teams receive a
                    read-only copy.
                  </li>

                  <li>
                    Teams can select a
                    week before
                    importing.
                  </li>

                  <li>
                    Disabling this option
                    stops sharing when
                    you save the post.
                  </li>
                </ul>
              </div>

              <div className="--LFGV2-share-card --LFGV2-share-card--visibility">
                <div className="--LFGV2-share-card__icon">
                  H
                </div>

                <div className="--LFGV2-share-card__copy">
                  <strong>
                    Hide from my Teams
                  </strong>

                  <span>
                    Hide this player
                    post from people who
                    are on any team you
                    currently belong to.
                  </span>
                </div>

                <label className="--LFGV2-toggle-row --LFGV2-toggle-row--compact">
                  <input
                    className="--LFGV2-toggle-row__native"
                    type="checkbox"
                    checked={
                      form.hideFromMyTeams
                    }
                    onChange={(event) =>
                      setForm({
                        ...form,
                        hideFromMyTeams:
                          event.target
                            .checked,
                      })
                    }
                  />

                  <span className="--LFGV2-toggle">
                    <span className="--LFGV2-toggle__thumb" />
                  </span>
                </label>
              </div>
            </div>
          )}

          {step === 4 && (
            <div className="--LFGV2-form-section">
              <div className="--LFGV2-form-section__heading">
                <h2>
                  Review Player Post
                </h2>

                <p>
                  Confirm the important
                  details before
                  publishing.
                </p>
              </div>

              <div className="--LFGV2-review-card">
                <div className="--LFGV2-review-card__hero">
                  <Avatar
                    name={
                      profileName ||
                      existingPost?.username ||
                      "Player"
                    }
                    size="large"
                  />

                  <div>
                    <h3>
                      {profileName ||
                        existingPost?.username ||
                        "Player"}
                    </h3>

                    <div className="--LFGV2-detail-card__badges">
                      <MetadataBadge>
                        {
                          form.userRegion
                        }
                      </MetadataBadge>

                      <MetadataBadge>
                        {
                          form.currentRank
                        }
                      </MetadataBadge>

                      <MetadataBadge variant="peak">
                        Peak{" "}
                        {
                          form.peakRank
                        }
                      </MetadataBadge>
                    </div>
                  </div>
                </div>

                <p>
                  {form.description ||
                    "No description provided."}
                </p>

                <div className="--LFGV2-review-card__line">
                  <span>
                    Top Agents
                  </span>

                  <strong>
                    {(
                      tiers[
                        "My Top 3 Agents"
                      ] || []
                    ).join(", ") || "—"}
                  </strong>
                </div>

                <div className="--LFGV2-review-card__line">
                  <span>
                    Availability
                  </span>

                  <strong>
                    {shareAvailability
                      ? "Shared"
                      : "Not shared"}
                  </strong>
                </div>

                <div className="--LFGV2-review-card__line">
                  <span>
                    Socials
                  </span>

                  <strong>
                    {
                      form.socials.filter(
                        (social) =>
                          social.label &&
                          social.url
                      ).length
                    }
                  </strong>
                </div>

                <div className="--LFGV2-review-card__line">
                  <span>
                    Team Visibility
                  </span>

                  <strong>
                    {form.hideFromMyTeams
                      ? "Hidden from my teams"
                      : "Visible to my teams"}
                  </strong>
                </div>
              </div>
            </div>
          )}

          <WizardFooter
            step={step}
            lastStep={
              steps.length - 1
            }
            onBack={() =>
              setStep((current) =>
                Math.max(
                  0,
                  current - 1
                )
              )
            }
            onNext={() =>
              setStep((current) =>
                Math.min(
                  steps.length - 1,
                  current + 1
                )
              )
            }
            onCancel={onCancel}
            onSave={submit}
            saveLabel={
              existingPost
                ? "Save Changes"
                : "Create Player Post"
            }
          />
        </div>
      </div>
    </div>
  );
}

/* ======================================================================
   TEAM POST EDITOR
   ====================================================================== */

function TeamPostEditor({
  teamId,
  existingPost = null,
  onCancel,
  onSaved,
  showToast,
  showError,
}) {
  const steps = [
    "Basic Info",
    "Roles & Roster",
    "Socials",
    "Questions",
    "Review",
  ];

  const [step, setStep] =
    useState(0);

  const [teamMeta, setTeamMeta] =
    useState(null);

  const [roster, setRoster] =
    useState([]);

  const [state, setState] =
    useState({
      teamRegion: "NA-E",
      teamType: "Premier Open",
      currentAvgRank:
        "Diamond II",
      teamDescription: "",
      teamSocials: [
        {
          label: "",
          url: "",
        },
      ],
      lookingForRoles: [],
      customQuestions: [],
    });

  useEffect(() => {
    const load = async () => {
      try {
        if (!teamId) {
          setTeamMeta(null);
          setRoster(
            await buildTeamRoster(
              {},
              existingPost?.teamMembers
            )
          );
          return;
        }

        const teamSnapshot =
          await getDoc(
            doc(db, "teams", teamId)
          );

        if (!teamSnapshot.exists()) {
          setTeamMeta(null);
          setRoster(
            await buildTeamRoster(
              {},
              existingPost?.teamMembers
            )
          );
          return;
        }

        const team =
          teamSnapshot.data();

        setTeamMeta(team);

        setState((previous) => ({
          ...previous,

          teamRegion:
            existingPost?.teamRegion ||
            team.teamRegion ||
            previous.teamRegion,

          teamType:
            existingPost?.teamType ||
            team.teamType ||
            previous.teamType,

          currentAvgRank:
            existingPost?.currentAvgRank ||
            previous.currentAvgRank,

          teamDescription:
            existingPost
              ? existingPost.teamDescription ||
                ""
              : team.teamDescription ||
                "",

          teamSocials:
            existingPost &&
            Array.isArray(
              existingPost.teamSocials
            ) &&
            existingPost.teamSocials
              .length
              ? existingPost.teamSocials
              : Array.isArray(
                    team.teamSocials
                  ) &&
                  team.teamSocials
                    .length
                ? team.teamSocials
                : [
                    {
                      label: "",
                      url: "",
                    },
                  ],

          lookingForRoles:
            existingPost?.lookingForRoles ||
            [],

          customQuestions:
            normalizeQuestionList(
              existingPost?.customQuestions
            ),
        }));

        setRoster(
          await buildTeamRoster(
            team,
            existingPost?.teamMembers
          )
        );
      } catch (error) {
        showError?.(error);
      }
    };

    load();
  }, [
    teamId,
    existingPost,
    showError,
  ]);

  const toggleRole = (role) => {
    setState((previous) => {
      const exists =
        previous.lookingForRoles.includes(
          role
        );

      return {
        ...previous,
        lookingForRoles: exists
          ? previous.lookingForRoles.filter(
              (currentRole) =>
                currentRole !== role
            )
          : [
              ...previous.lookingForRoles,
              role,
            ],
      };
    });
  };

  const updateSocial = (
    index,
    key,
    value
  ) => {
    setState((previous) => {
      const next = [
        ...previous.teamSocials,
      ];

      next[index] = {
        ...next[index],
        [key]: value,
      };

      return {
        ...previous,
        teamSocials: next,
      };
    });
  };

  const addSocial = () => {
    setState((previous) => {
      if (
        previous.teamSocials.length >=
        5
      ) {
        return previous;
      }

      return {
        ...previous,
        teamSocials: [
          ...previous.teamSocials,
          {
            label: "",
            url: "",
          },
        ],
      };
    });
  };

  const removeSocial = (index) => {
    setState((previous) => ({
      ...previous,

      teamSocials:
        previous.teamSocials.filter(
          (_, itemIndex) =>
            itemIndex !== index
        ),
    }));
  };

  const updateQuestion = (
    index,
    value
  ) => {
    setState((previous) => {
      const next = [
        ...previous.customQuestions,
      ];

      next[index] = clampChars(
        value,
        250
      );

      return {
        ...previous,
        customQuestions: next,
      };
    });
  };

  const addQuestion = () => {
    setState((previous) => {
      if (
        previous.customQuestions.length >=
        5
      ) {
        return previous;
      }

      return {
        ...previous,
        customQuestions: [
          ...previous.customQuestions,
          "",
        ],
      };
    });
  };

  const removeQuestion = (index) => {
    setState((previous) => ({
      ...previous,
      customQuestions:
        previous.customQuestions.filter(
          (_, itemIndex) =>
            itemIndex !== index
        ),
    }));
  };

  const submit = async () => {
    if (!auth.currentUser) {
      showToast?.(
        "Please log in first.",
        "warning"
      );

      return;
    }

    if (
      !teamId ||
      (!teamMeta && !existingPost)
    ) {
      showToast?.(
        "Team information is missing.",
        "warning"
      );

      return;
    }

    try {
      const latestTeamSnapshot =
        await getDoc(
          doc(db, "teams", teamId)
        );

      const latestTeam =
        latestTeamSnapshot.exists()
          ? latestTeamSnapshot.data()
          : teamMeta || {};

      const enriched =
        await buildTeamRoster(
          latestTeam,
          existingPost?.teamMembers
        );

      const payload = {
        type: "team",

        teamId:
          existingPost?.teamId ||
          teamId,

        teamName:
          latestTeam.teamName ||
          latestTeam.name ||
          existingPost?.teamName ||
          "Team",

        teamRegion:
          state.teamRegion,

        teamType: state.teamType,

        currentAvgRank:
          state.currentAvgRank,

        teamDescription:
          clampWords(
            state.teamDescription,
            120
          ),

        teamSocials:
          state.teamSocials
            .filter(
              (social) =>
                social.label &&
                social.url
            )
            .slice(0, 5),

        lookingForRoles:
          state.lookingForRoles,

        customQuestions:
          normalizeQuestionList(
            state.customQuestions
          ),

        teamMembers: enriched,

        updatedAt:
          serverTimestamp(),

        isOpen:
          existingPost?.isOpen ??
          true,
      };

      if (existingPost?.id) {
        await updateDoc(
          lfgPostDoc(
            db,
            existingPost.id
          ),
          payload
        );
      } else {
        await setDoc(
          doc(lfgPostsCollection(db)),
          {
            ...payload,

            createdBy:
              auth.currentUser.uid,

            createdAt:
              serverTimestamp(),
          }
        );
      }

      showToast?.(
        existingPost
          ? "Team LFG updated."
          : "Team LFG created.",
        "success"
      );

      onSaved?.();
    } catch (error) {
      showError?.(error);
    }
  };

  return (
    <div className="--LFGV2-editor-page">
      <div className="--LFGV2-editor-page__heading">
        <div>
          <span className="--LFGV2-page-kicker">
            Team Recruitment
          </span>

          <h1 className="--LFGV2-page-title">
            {existingPost
              ? "Edit Team Post"
              : "Create Team Post"}
          </h1>

          <p className="--LFGV2-page-subtitle">
            Show players who you are
            and exactly what your team
            needs.
          </p>
        </div>
      </div>

      <div className="--LFGV2-wizard-layout">
        <WizardSidebar
          title="Team Post"
          steps={steps}
          activeStep={step}
          onStepSelect={setStep}
        />

        <div className="--LFGV2-wizard-content">
          {step === 0 && (
            <div className="--LFGV2-form-section">
              <div className="--LFGV2-form-section__heading">
                <h2>
                  Team Information
                </h2>

                <p>
                  Core recruitment
                  information shown on
                  the LFG feed.
                </p>
              </div>

              <div className="--LFGV2-form-grid --LFGV2-form-grid--two">
                <label className="--LFGV2-field">
                  <span>
                    Team Name
                  </span>

                  <input
                    className="--LFGV2-input"
                    readOnly
                    value={
                      teamMeta?.teamName ||
                      teamMeta?.name ||
                      existingPost?.teamName ||
                      ""
                    }
                  />
                </label>

                <label className="--LFGV2-field">
                  <span>
                    Server Region
                  </span>

                  <select
                    className="--LFGV2-input"
                    value={
                      state.teamRegion
                    }
                    onChange={(event) =>
                      setState({
                        ...state,
                        teamRegion:
                          event.target
                            .value,
                      })
                    }
                  >
                    {REGIONS.map(
                      (region) => (
                        <option
                          key={region}
                          value={region}
                        >
                          {region}
                        </option>
                      )
                    )}
                  </select>
                </label>

                <label className="--LFGV2-field">
                  <span>
                    Team Type
                  </span>

                  <select
                    className="--LFGV2-input"
                    value={
                      state.teamType
                    }
                    onChange={(event) =>
                      setState({
                        ...state,
                        teamType:
                          event.target
                            .value,
                      })
                    }
                  >
                    {TEAM_TYPES.map(
                      (type) => (
                        <option
                          key={type}
                          value={type}
                        >
                          {type}
                        </option>
                      )
                    )}
                  </select>
                </label>

                <label className="--LFGV2-field">
                  <span>
                    Average Team Rank
                  </span>

                  <select
                    className="--LFGV2-input"
                    value={
                      state.currentAvgRank
                    }
                    onChange={(event) =>
                      setState({
                        ...state,
                        currentAvgRank:
                          event.target
                            .value,
                      })
                    }
                  >
                    {RANKS.map(
                      (rank) => (
                        <option
                          key={rank}
                          value={rank}
                        >
                          {rank}
                        </option>
                      )
                    )}
                  </select>
                </label>
              </div>

              <label className="--LFGV2-field">
                <span>
                  Team Description
                  <small>
                    Maximum 120 words
                  </small>
                </span>

                <textarea
                  className="--LFGV2-input --LFGV2-textarea"
                  value={
                    state.teamDescription
                  }
                  onChange={(event) =>
                    setState({
                      ...state,
                      teamDescription:
                        clampWords(
                          event.target
                            .value,
                          120
                        ),
                    })
                  }
                  onKeyDownCapture={
                    stopSpacePropagation
                  }
                  placeholder="Describe your team, goals, schedule and culture."
                />
              </label>
            </div>
          )}

          {step === 1 && (
            <div className="--LFGV2-form-section">
              <div className="--LFGV2-form-section__heading">
                <h2>
                  Roster & Roles
                </h2>

                <p>
                  Your roster is synced
                  from your team. Select
                  the roles you're
                  currently recruiting.
                </p>
              </div>

              <div className="--LFGV2-team-editor-grid">
                <div className="--LFGV2-team-roster-card">
                  <div className="--LFGV2-card-header">
                    <h3>
                      Current Roster
                    </h3>

                    <span className="--LFGV2-count-badge">
                      {roster.length}
                    </span>
                  </div>

                  <div className="--LFGV2-roster-list">
                    {roster.length ? (
                      roster.map(
                        (member) => (
                          <div
                            key={
                              member.uid ||
                              member.username
                            }
                            className="--LFGV2-roster-row"
                          >
                            <div className="--LFGV2-roster-row__user">
                              <Avatar
                                name={
                                  member.username
                                }
                                size="small"
                                tooltip={
                                  member.username
                                }
                              />

                              <span>
                                {
                                  member.username
                                }
                              </span>
                            </div>

                            <RoleBadge
                              role={
                                member.role
                              }
                            />
                          </div>
                        )
                      )
                    ) : (
                      <span className="--LFGV2-muted">
                        No roster members
                        were found for this
                        team yet.
                      </span>
                    )}
                  </div>
                </div>

                <div className="--LFGV2-team-role-card">
                  <div className="--LFGV2-card-header">
                    <h3>
                      Looking For
                    </h3>

                    <span>
                      Select roles
                    </span>
                  </div>

                  <div className="--LFGV2-role-selector">
                    {ROLES.map(
                      (role) => (
                        <button
                          key={role}
                          type="button"
                          className={
                            state.lookingForRoles.includes(
                              role
                            )
                              ? "--LFGV2-role-option --LFGV2-role-option--active"
                              : "--LFGV2-role-option"
                          }
                          onClick={() =>
                            toggleRole(
                              role
                            )
                          }
                        >
                          {role}
                        </button>
                      )
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="--LFGV2-form-section">
              <div className="--LFGV2-form-section__heading">
                <h2>
                  Team Socials
                </h2>

                <p>
                  Add up to five ways
                  players can learn more
                  about or contact your
                  team.
                </p>
              </div>

              <div className="--LFGV2-social-editor">
                {state.teamSocials.map(
                  (
                    social,
                    index
                  ) => (
                    <div
                      key={index}
                      className="--LFGV2-social-editor__row"
                    >
                      <input
                        className="--LFGV2-input"
                        placeholder="Discord, Twitter, Website..."
                        value={
                          social.label
                        }
                        onChange={(
                          event
                        ) =>
                          updateSocial(
                            index,
                            "label",
                            event.target
                              .value
                          )
                        }
                        onKeyDownCapture={
                          stopSpacePropagation
                        }
                      />

                      <input
                        className="--LFGV2-input"
                        placeholder="Username or https://..."
                        value={
                          social.url
                        }
                        onChange={(
                          event
                        ) =>
                          updateSocial(
                            index,
                            "url",
                            event.target
                              .value
                          )
                        }
                        onKeyDownCapture={
                          stopSpacePropagation
                        }
                      />

                      <button
                        type="button"
                        className="--LFGV2-icon-btn --LFGV2-icon-btn--danger app-cancel-control"
                        onClick={() =>
                          removeSocial(
                            index
                          )
                        }
                      >
                        ×
                      </button>
                    </div>
                  )
                )}

                <button
                  type="button"
                  className="--LFGV2-btn --LFGV2-btn--secondary"
                  onClick={addSocial}
                  disabled={
                    state.teamSocials
                      .length >= 5
                  }
                >
                  + Add Social
                </button>
              </div>
            </div>
          )}

          {step === 3 && (
            <div className="--LFGV2-form-section">
              <div className="--LFGV2-form-section__heading">
                <h2>
                  Custom Application
                  Questions
                </h2>

                <p>
                  Add up to five
                  questions for players
                  to answer in chat when
                  they connect to this
                  team post.
                </p>
              </div>

              <div className="--LFGV2-question-editor">
                <div className="--LFGV2-form-subsection__head">
                  <div>
                    <h3>
                      Questions
                    </h3>

                    <p>
                      Each question is
                      limited to 250
                      characters.
                    </p>
                  </div>

                  <button
                    type="button"
                    className="--LFGV2-btn --LFGV2-btn--secondary"
                    onClick={addQuestion}
                    disabled={
                      state.customQuestions
                        .length >= 5
                    }
                  >
                    + Add Question
                  </button>
                </div>

                {state.customQuestions.length ? (
                  state.customQuestions.map(
                    (
                      question,
                      index
                    ) => (
                      <div
                        key={`team-question-${index}`}
                        className="--LFGV2-question-editor__row"
                      >
                        <label className="--LFGV2-field">
                          <span>
                            Question{" "}
                            {index + 1}
                          </span>

                          <textarea
                            className="--LFGV2-input --LFGV2-textarea --LFGV2-textarea--compact"
                            value={
                              question
                            }
                            maxLength={
                              250
                            }
                            onChange={(
                              event
                            ) =>
                              updateQuestion(
                                index,
                                event.target
                                  .value
                              )
                            }
                            onKeyDownCapture={
                              stopSpacePropagation
                            }
                            placeholder="Ask about schedule, role fit, goals or anything else applicants should answer."
                          />
                        </label>

                        <div className="--LFGV2-question-editor__actions">
                          <span className="--LFGV2-character-count">
                            {
                              question.length
                            }
                            /250
                          </span>

                          <button
                            type="button"
                            className="--LFGV2-icon-btn --LFGV2-icon-btn--danger"
                            onClick={() =>
                              removeQuestion(
                                index
                              )
                            }
                            aria-label={`Remove question ${index + 1}`}
                          >
                            Ã—
                          </button>
                        </div>
                      </div>
                    )
                  )
                ) : (
                  <div className="--LFGV2-info-card">
                    No custom questions
                    added yet. Applicants
                    will still be able to
                    connect normally.
                  </div>
                )}
              </div>
            </div>
          )}

          {step === 4 && (
            <div className="--LFGV2-form-section">
              <div className="--LFGV2-form-section__heading">
                <h2>
                  Review Team Post
                </h2>

                <p>
                  Confirm your
                  recruitment post
                  before publishing.
                </p>
              </div>

              <div className="--LFGV2-review-card">
                <div className="--LFGV2-review-card__hero">
                  <Avatar
                    name={
                      teamMeta?.teamName ||
                      teamMeta?.name ||
                      existingPost?.teamName ||
                      "Team"
                    }
                    size="large"
                  />

                  <div>
                    <h3>
                      {teamMeta?.teamName ||
                        teamMeta?.name ||
                        existingPost?.teamName ||
                        "Team"}
                    </h3>

                    <div className="--LFGV2-detail-card__badges">
                      <MetadataBadge>
                        {
                          state.teamRegion
                        }
                      </MetadataBadge>

                      <MetadataBadge>
                        {
                          state.teamType
                        }
                      </MetadataBadge>

                      <MetadataBadge>
                        {
                          state.currentAvgRank
                        }{" "}
                        Avg.
                      </MetadataBadge>
                    </div>
                  </div>
                </div>

                <p>
                  {state.teamDescription ||
                    "No description provided."}
                </p>

                <div className="--LFGV2-review-card__line">
                  <span>
                    Looking For
                  </span>

                  <strong>
                    {state.lookingForRoles.join(
                      ", "
                    ) || "No roles selected"}
                  </strong>
                </div>

                <div className="--LFGV2-review-card__line">
                  <span>
                    Roster
                  </span>

                  <strong>
                    {roster.length}{" "}
                    member
                    {roster.length === 1
                      ? ""
                      : "s"}
                  </strong>
                </div>

                <div className="--LFGV2-review-card__line">
                  <span>
                    Socials
                  </span>

                  <strong>
                    {
                      state.teamSocials.filter(
                        (social) =>
                          social.label &&
                          social.url
                      ).length
                    }
                  </strong>
                </div>

                <div className="--LFGV2-review-card__line">
                  <span>
                    Questions
                  </span>

                  <strong>
                    {
                      normalizeQuestionList(
                        state.customQuestions
                      ).length
                    }
                  </strong>
                </div>
              </div>
            </div>
          )}

          <WizardFooter
            step={step}
            lastStep={
              steps.length - 1
            }
            onBack={() =>
              setStep((current) =>
                Math.max(
                  0,
                  current - 1
                )
              )
            }
            onNext={() =>
              setStep((current) =>
                Math.min(
                  steps.length - 1,
                  current + 1
                )
              )
            }
            onCancel={onCancel}
            onSave={submit}
            saveLabel={
              existingPost
                ? "Save Changes"
                : "Create Team Post"
            }
          />
        </div>
      </div>
    </div>
  );
}

/* ======================================================================
   CHAT ROOM
   ====================================================================== */

function LFGChatRoom({
  chatId,
  teamNamePrefixMap,
  onClose,
  onDelete,
  onViewPost,
  showToast,
  showError,
}) {
  const [messages, setMessages] =
    useState([]);

  const [text, setText] =
    useState("");

  const [chatInfo, setChatInfo] =
    useState(null);

  const [
    questionnaireOpen,
    setQuestionnaireOpen,
  ] = useState(false);

  const [
    questionnaireAnswers,
    setQuestionnaireAnswers,
  ] = useState([]);

  const [
    questionnaireSubmitting,
    setQuestionnaireSubmitting,
  ] = useState(false);

  const endRef = useRef(null);

  const currentUid =
    auth.currentUser?.uid || null;

  const teamQuestions = useMemo(
    () =>
      normalizeQuestionList(
        chatInfo?.teamQuestions
      ),
    [chatInfo?.teamQuestions]
  );

  const canSendQuestionnaire =
    chatInfo?.direction ===
      "player_to_team" &&
    currentUid ===
      chatInfo?.playerId &&
    teamQuestions.length > 0;

  const latestQuestionnaire =
    useMemo(
      () =>
        [...messages]
          .reverse()
          .find(
            (message) =>
              message.type ===
                "questionnaire" &&
              message.senderId ===
                currentUid
          ) || null,
      [messages, currentUid]
    );

  useEffect(() => {
    if (!chatId) {
      setMessages([]);
      setChatInfo(null);
      return undefined;
    }

    const unsubscribeChat =
      onSnapshot(
        lfgChatDoc(db, chatId),
        (snapshot) => {
          if (!snapshot.exists()) {
            onClose?.();
            return;
          }

          setChatInfo(
            snapshot.data()
          );
        }
      );

    const messagesQuery =
      query(
        lfgChatMessagesCollection(
          db,
          chatId
        ),
        orderBy("createdAt", "asc")
      );

    const unsubscribeMessages =
      onSnapshot(
        messagesQuery,
        (snapshot) => {
          setMessages(
            snapshot.docs.map(
              (messageDoc) => ({
                id: messageDoc.id,
                ...messageDoc.data(),
              })
            )
          );
        }
      );

    return () => {
      unsubscribeChat();
      unsubscribeMessages();
    };
  }, [chatId, onClose]);

  useEffect(() => {
    endRef.current?.scrollIntoView?.({
      behavior: "smooth",
    });
  }, [messages]);

  useEffect(() => {
    setQuestionnaireOpen(false);
    setQuestionnaireAnswers([]);
    setQuestionnaireSubmitting(false);
  }, [chatId]);

  const send = async () => {
    if (!auth.currentUser) {
      showToast?.(
        "Please log in first.",
        "warning"
      );

      return;
    }

    if (!text.trim()) {
      return;
    }

    try {
      const capped =
        clampWords(text, 150);

      const deleteAfter =
        new Date(
          Date.now() +
            14 *
              24 *
              60 *
              60 *
              1000
        );

      const userSnapshot =
        await getDoc(
          doc(
            db,
            "users",
            auth.currentUser.uid
          )
        );

      const username =
        userSnapshot.exists()
          ? userSnapshot.data()
              .username ||
            "Unknown"
          : "Unknown";

      const senderTeamName =
        teamNamePrefixMap?.[
          auth.currentUser.uid
        ];

      await addDoc(
        lfgChatMessagesCollection(
          db,
          chatId
        ),
        {
          ...(senderTeamName
            ? {
                senderTeamName,
              }
            : {}),

          senderId:
            auth.currentUser.uid,

          senderUsername:
            username,

          text: capped,

          createdAt:
            serverTimestamp(),

          deleteAfter,
        }
      );

      await updateDoc(
        lfgChatDoc(
          db,
          chatId
        ),
        {
          lastMessageAt:
            serverTimestamp(),
        }
      );

      setText("");
    } catch (error) {
      showError?.(error);
    }
  };

  const openQuestionnaire = () => {
    const seededAnswers =
      latestQuestionnaire &&
      Array.isArray(
        latestQuestionnaire.questionnaire
      ) &&
      latestQuestionnaire.questionnaire
        .length === teamQuestions.length
        ? latestQuestionnaire.questionnaire.map(
            (entry) =>
              clampChars(
                entry?.answer || "",
                250
              )
          )
        : teamQuestions.map(() => "");

    setQuestionnaireAnswers(
      seededAnswers
    );
    setQuestionnaireOpen(true);
  };

  const submitQuestionnaire =
    async () => {
      if (
        !auth.currentUser ||
        !chatId ||
        !teamQuestions.length
      ) {
        return;
      }

      const questionnaire =
        teamQuestions.map(
          (question, index) => ({
            question,
            answer: trimText(
              clampChars(
                questionnaireAnswers[
                  index
                ] || "",
                250
              )
            ),
          })
        );

      if (
        questionnaire.some(
          (entry) => !entry.answer
        )
      ) {
        showToast?.(
          "Please answer every question before sending.",
          "warning"
        );
        return;
      }

      try {
        setQuestionnaireSubmitting(
          true
        );

        const deleteAfter =
          new Date(
            Date.now() +
              14 *
                24 *
                60 *
                60 *
                1000
          );

        const userSnapshot =
          await getDoc(
            doc(
              db,
              "users",
              auth.currentUser.uid
            )
          );

        const username =
          userSnapshot.exists()
            ? userSnapshot.data()
                .username ||
              "Unknown"
            : "Unknown";

        const senderTeamName =
          teamNamePrefixMap?.[
            auth.currentUser.uid
          ];

        await addDoc(
          lfgChatMessagesCollection(
            db,
            chatId
          ),
          {
            ...(senderTeamName
              ? {
                  senderTeamName,
                }
              : {}),
            senderId:
              auth.currentUser.uid,
            senderUsername:
              username,
            type: "questionnaire",
            text: "Submitted application questionnaire.",
            questionnaire,
            createdAt:
              serverTimestamp(),
            deleteAfter,
          }
        );

        await updateDoc(
          lfgChatDoc(
            db,
            chatId
          ),
          {
            lastMessageAt:
              serverTimestamp(),
          }
        );

        setQuestionnaireOpen(
          false
        );
        showToast?.(
          "Questionnaire sent.",
          "success"
        );
      } catch (error) {
        showError?.(error);
      } finally {
        setQuestionnaireSubmitting(
          false
        );
      }
    };

  if (!chatId) {
    return (
      <div className="--LFGV2-chat-empty">
        <div className="--LFGV2-chat-empty__icon">
          ✉
        </div>

        <h3>
          Select a conversation
        </h3>

        <p>
          Open one of your LFG chats to
          continue the conversation.
        </p>
      </div>
    );
  }

  return (
    <section className="--LFGV2-chat-room">
      <header className="--LFGV2-chat-room__header">
        <div className="--LFGV2-chat-room__identity">
          <Avatar
            name={
              chatInfo?.teamName ||
              chatInfo?.playerUsername ||
              "LFG"
            }
            size="medium"
          />

          <div>
            <h2>
              {chatInfo?.teamName ||
                "LFG Chat"}
            </h2>

            <span>
              {chatInfo?.direction ===
              "player_to_team"
                ? "Player → Team"
                : "Team → Player"}
            </span>
          </div>
        </div>

        <div className="--LFGV2-chat-room__header-actions">
          {chatInfo?.teamPostId && (
            <button
              type="button"
              className="--LFGV2-btn --LFGV2-btn--secondary"
              onClick={() =>
                onViewPost(
                  chatInfo.teamPostId
                )
              }
            >
              View Team Post
            </button>
          )}

          {canSendQuestionnaire && (
            <button
              type="button"
              className="--LFGV2-btn --LFGV2-btn--primary"
              onClick={
                openQuestionnaire
              }
            >
              {latestQuestionnaire
                ? "Resend Questionnaire"
                : "Send Questionnaire"}
            </button>
          )}

          {chatInfo?.userPostId && (
            <button
              type="button"
              className="--LFGV2-btn --LFGV2-btn--secondary"
              onClick={() =>
                onViewPost(
                  chatInfo.userPostId
                )
              }
            >
              View Player Post
            </button>
          )}

          <button
            type="button"
            className="--LFGV2-icon-btn app-cancel-control"
            onClick={onClose}
            aria-label="Close conversation"
          >
            ×
          </button>
        </div>
      </header>

      <div className="--LFGV2-chat-room__retention">
        Messages are retained for 14
        days and are then deleted.
      </div>

      <div className="--LFGV2-chat-room__messages">
        {messages.length === 0 ? (
          <EmptyState
            title="No messages yet"
            description="Start the conversation when you're ready."
          />
        ) : (
          messages.map((message) => {
            const mine =
              message.senderId ===
              currentUid;

            const isQuestionnaire =
              message.type ===
                "questionnaire" &&
              Array.isArray(
                message.questionnaire
              );

            return (
              <div
                key={message.id}
                className={
                  mine
                    ? "--LFGV2-message --LFGV2-message--mine"
                    : "--LFGV2-message"
                }
              >
                {!mine && (
                  <Avatar
                    name={
                      message.senderUsername
                    }
                    size="tiny"
                  />
                )}

                <div className="--LFGV2-message__content">
                  <div className="--LFGV2-message__sender">
                    {message.senderTeamName
                      ? `[${message.senderTeamName}] `
                      : ""}

                    {
                      message.senderUsername
                    }
                  </div>

                  <div
                    className={
                      isQuestionnaire
                        ? "--LFGV2-message__bubble --LFGV2-message__bubble--questionnaire"
                        : "--LFGV2-message__bubble"
                    }
                  >
                    {isQuestionnaire ? (
                      <div className="--LFGV2-questionnaire-message">
                        <strong className="--LFGV2-questionnaire-message__title">
                          {
                            message.text
                          }
                        </strong>

                        <div className="--LFGV2-questionnaire-message__list">
                          {message.questionnaire.map(
                            (
                              entry,
                              index
                            ) => (
                              <div
                                key={`${message.id}-qa-${index}`}
                                className="--LFGV2-questionnaire-message__item"
                              >
                                <span className="--LFGV2-questionnaire-message__question">
                                  {
                                    entry.question
                                  }
                                </span>

                                <span className="--LFGV2-questionnaire-message__answer">
                                  {
                                    entry.answer
                                  }
                                </span>
                              </div>
                            )
                          )}
                        </div>
                      </div>
                    ) : (
                      message.text
                    )}
                  </div>
                </div>
              </div>
            );
          })
        )}

        <div ref={endRef} />
      </div>

      <footer className="--LFGV2-chat-room__composer">
        <input
          className="--LFGV2-input --LFGV2-chat-room__input"
          value={text}
          placeholder="Type a message..."
          onChange={(event) =>
            setText(
              event.target.value
            )
          }
          onKeyDownCapture={
            stopSpacePropagation
          }
          onKeyDown={(event) => {
            if (
              event.key === "Enter" &&
              !event.shiftKey
            ) {
              event.preventDefault();
              send();
            }
          }}
        />

        <button
          type="button"
          className="--LFGV2-btn --LFGV2-btn--primary --LFGV2-chat-room__send"
          onClick={send}
        >
          Send
        </button>
      </footer>

      <div className="--LFGV2-chat-room__danger">
        <button
          type="button"
          className="--LFGV2-btn --LFGV2-btn--danger-soft"
          onClick={onDelete}
        >
          Delete Conversation
        </button>
      </div>
      <Modal
        open={questionnaireOpen}
        title="Team Questionnaire"
        onClose={() =>
          !questionnaireSubmitting &&
          setQuestionnaireOpen(
            false
          )
        }
      >
        <div className="--LFGV2-modal-stack">
          <div className="--LFGV2-info-card">
            Answer every question.
            Each response is limited
            to 250 characters and will
            be sent into the chat.
          </div>

          {teamQuestions.map(
            (question, index) => (
              <label
                key={`chat-question-${index}`}
                className="--LFGV2-field"
              >
                <span>
                  Question {index + 1}
                </span>

                <div className="--LFGV2-question-chip --LFGV2-question-chip--stacked">
                  <strong>
                    {question}
                  </strong>

                  <textarea
                    className="--LFGV2-input --LFGV2-textarea --LFGV2-textarea--compact"
                    value={
                      questionnaireAnswers[
                        index
                      ] || ""
                    }
                    maxLength={250}
                    onChange={(event) =>
                      setQuestionnaireAnswers(
                        (previous) => {
                          const next =
                            [
                              ...previous,
                            ];

                          next[index] =
                            clampChars(
                              event
                                .target
                                .value,
                              250
                            );

                          return next;
                        }
                      )
                    }
                    onKeyDownCapture={
                      stopSpacePropagation
                    }
                    placeholder="Write your answer..."
                  />
                </div>

                <span className="--LFGV2-character-count">
                  {(
                    questionnaireAnswers[
                      index
                    ] || ""
                  ).length}
                  /250
                </span>
              </label>
            )
          )}

          <div className="--LFGV2-modal-actions">
            <button
              type="button"
              className="--LFGV2-btn --LFGV2-btn--secondary app-cancel-control"
              disabled={
                questionnaireSubmitting
              }
              onClick={() =>
                setQuestionnaireOpen(
                  false
                )
              }
            >
              Cancel
            </button>

            <button
              type="button"
              className="--LFGV2-btn --LFGV2-btn--primary"
              disabled={
                questionnaireSubmitting
              }
              onClick={
                submitQuestionnaire
              }
            >
              {questionnaireSubmitting
                ? "Sending..."
                : "Send Questionnaire"}
            </button>
          </div>
        </div>
      </Modal>
    </section>
  );
}

/* ======================================================================
   CHAT LIST
   ====================================================================== */

function ChatList({
  chats,
  selectedChatId,
  onOpen,
  onDelete,
  compact = false,
}) {
  if (!chats.length) {
    return (
      <div className="--LFGV2-chat-list__empty">
        No conversations yet.
      </div>
    );
  }

  return (
    <div
      className={
        compact
          ? "--LFGV2-chat-list --LFGV2-chat-list--compact"
          : "--LFGV2-chat-list"
      }
    >
      {chats.map((chat) => (
        <div
          key={chat.id}
          className={
            selectedChatId === chat.id
              ? "--LFGV2-chat-list__item --LFGV2-chat-list__item--active"
              : "--LFGV2-chat-list__item"
          }
        >
          <button
            type="button"
            className="--LFGV2-chat-list__open"
            onClick={() =>
              onOpen(chat)
            }
          >
            <Avatar
              name={
                chat.teamName ||
                chat.playerUsername ||
                "LFG"
              }
              size="small"
            />

            <span className="--LFGV2-chat-list__copy">
              <strong>
                {chat.teamName ||
                  chat.playerUsername ||
                  "LFG Chat"}
              </strong>

              <small>
                {chat.direction ===
                "player_to_team"
                  ? "Player → Team"
                  : "Team → Player"}
              </small>
            </span>

            <span className="--LFGV2-chat-list__time">
              {formatRelativeActivity(
                chat.lastMessageAt
              )}
            </span>
          </button>

          {!compact && (
            <button
              type="button"
              className="--LFGV2-icon-btn --LFGV2-icon-btn--danger app-cancel-control"
              onClick={() =>
                onDelete(chat)
              }
              aria-label="Delete conversation"
            >
              ×
            </button>
          )}
        </div>
      ))}
    </div>
  );
}

/* ======================================================================
   MODAL
   ====================================================================== */

function Modal({
  open,
  title,
  children,
  onClose,
  className = "",
}) {
  if (!open) {
    return null;
  }

  return (
    <div
      className="--LFGV2-modal-backdrop"
      role="dialog"
      aria-modal="true"
      aria-label={title}
    >
      <div
        className={
          className
            ? `--LFGV2-modal ${className}`
            : "--LFGV2-modal"
        }
      >
        <header className="--LFGV2-modal__header">
          <h2>
            {title}
          </h2>

          <button
            type="button"
            className="--LFGV2-icon-btn app-cancel-control"
            onClick={onClose}
            aria-label="Close"
          >
            ×
          </button>
        </header>

        <div className="--LFGV2-modal__body">
          {children}
        </div>
      </div>
    </div>
  );
}

/* ======================================================================
   MAIN PAGE
   ====================================================================== */

export default function LFGPage() {
  const {
    teamId,
    teamName,
  } = useTeam();
  const isDesktopShell =
    typeof window !== "undefined" &&
    !!window.desktopAPI;

  const [uid, setUid] =
    useState(null);

  const [
    viewerTeamIds,
    setViewerTeamIds,
  ] = useState([]);

  const [
    activeSection,
    setActiveSection,
  ] = useState("feed");

  const [
    editorState,
    setEditorState,
  ] = useState({
    type: null,
    post: null,
  });

  const [
    detailPost,
    setDetailPost,
  ] = useState(null);

  const [posts, setPosts] =
    useState([]);

  const [
    liveUsernamesByUid,
    setLiveUsernamesByUid,
  ] = useState({});

  const [filters, setFilters] =
    useState({
      types: [],
      regions: [],
      ranks: [],
      roles: [],
      q: "",
      showHiddenMine:
        readLfgSettings()
          .showHiddenMine,
    });

  const [sortOrder, setSortOrder] =
    useState("newest");

  const [
    myTeamMemberUids,
    setMyTeamMemberUids,
  ] = useState([]);

  const [
    myTeamLeaderId,
    setMyTeamLeaderId,
  ] = useState(null);

  const [chatsUser, setChatsUser] =
    useState([]);

  const [chatsTeam, setChatsTeam] =
    useState([]);

  const [chat, setChat] =
    useState({
      open: false,
      id: null,
      teamMap: {},
    });

  const [
    openMenuForPostId,
    setOpenMenuForPostId,
  ] = useState(null);

  const [toast, setToast] =
    useState({
      open: false,
      message: "",
      type: "info",
    });

  const [confirm, setConfirm] =
    useState({
      open: false,
      message: "",
      onConfirm: null,
    });

  const [
    reportModal,
    setReportModal,
  ] = useState({
    open: false,
    post: null,
    reason: "",
    otherText: "",
  });

  const [
    submittedReports,
    setSubmittedReports,
  ] = useState([]);

  const [
    importAvailModal,
    setImportAvailModal,
  ] = useState({
    open: false,
    playerPost: null,
    dateISO:
      new Date()
        .toISOString()
        .slice(0, 10),
    loading: false,
  });

  const [
    savedPostIds,
    setSavedPostIds,
  ] = useState(() => {
    try {
      return readSavedPostIds();
    } catch (_) {
      return [];
    }
  });

  const applyShowHiddenMine =
    useCallback(
      (nextValue) => {
        setFilters((current) => ({
          ...current,
          showHiddenMine:
            nextValue === true,
        }));

        saveLfgSettings({
          showHiddenMine:
            nextValue === true,
        });
      },
      []
    );

  useEffect(() => {
    if (typeof window === "undefined") {
      return undefined;
    }

    const syncLfgSettings =
      () => {
        const nextSettings =
          readLfgSettings();

        setFilters((current) =>
          current.showHiddenMine ===
          nextSettings.showHiddenMine
            ? current
            : {
                ...current,
                showHiddenMine:
                  nextSettings.showHiddenMine,
              }
        );
      };

    const handleStorage =
      (event) => {
        if (
          !event.key ||
          event.key ===
            LFG_SETTINGS_STORAGE_KEY
        ) {
          syncLfgSettings();
        }
      };

    window.addEventListener(
      "storage",
      handleStorage
    );
    window.addEventListener(
      LFG_SETTINGS_UPDATED_EVENT,
      syncLfgSettings
    );

    return () => {
      window.removeEventListener(
        "storage",
        handleStorage
      );
      window.removeEventListener(
        LFG_SETTINGS_UPDATED_EVENT,
        syncLfgSettings
      );
    };
  }, []);

  const showToast = useCallback(
    (
      message,
      type = "info"
    ) => {
      setToast({
        open: true,
        message,
        type,
      });
    },
    []
  );

  const showError = useCallback(
    (
      error,
      fallback =
        "Something went wrong."
    ) => {
      console.error(error);

      showToast(
        prettyFirestoreError(
          error
        ) || fallback,
        "error"
      );
    },
    [showToast]
  );

  const myChats = useMemo(() => {
    const byId = new Map();

    [
      ...chatsUser,
      ...chatsTeam,
    ].forEach((conversation) => {
      byId.set(
        conversation.id,
        conversation
      );
    });

    return [
      ...byId.values(),
    ].sort(
      (first, second) =>
        (second.lastMessageAt?.toMillis?.() ||
          0) -
        (first.lastMessageAt?.toMillis?.() ||
          0)
    );
  }, [
    chatsUser,
    chatsTeam,
  ]);

  const resolvedPosts = useMemo(
    () =>
      posts.map((post) =>
        applyLiveIdentityToPost(
          post,
          liveUsernamesByUid
        )
      ),
    [posts, liveUsernamesByUid]
  );

  const savedPosts = useMemo(
    () =>
      resolvedPosts.filter(
        (post) =>
          savedPostIds.includes(post.id) &&
          canViewerSeePost(
            post,
            uid,
            viewerTeamIds
          )
      ),
    [
      resolvedPosts,
      savedPostIds,
      uid,
      viewerTeamIds,
    ]
  );

  const managedPosts = useMemo(
    () =>
      resolvedPosts.filter((post) =>
        canManagePost(
          post,
          uid,
          teamId || null
        )
      ),
    [
      resolvedPosts,
      uid,
      teamId,
    ]
  );

  useEffect(() => {
    const unsubscribe =
      onAuthStateChanged(
        auth,
        (user) => {
          setUid(user?.uid || null);
        }
      );

    return () => unsubscribe();
  }, []);

  useEffect(() => {
    if (!uid) {
      setViewerTeamIds([]);
      return undefined;
    }

    const unsubscribe =
      onSnapshot(
        doc(db, "users", uid),
        (snapshot) => {
          if (!snapshot.exists()) {
            setViewerTeamIds([]);
            return;
          }

          const userData =
            snapshot.data() || {};

          setViewerTeamIds(
            gatherUserTeamIds(
              userData,
              teamId ||
                userData.activeTeamId ||
                userData.teamId ||
                null
            )
          );
        },
        () => {
          setViewerTeamIds([]);
        }
      );

    return () => unsubscribe();
  }, [uid, teamId]);

  useEffect(() => {
    const postsQuery =
      query(
        lfgPostsCollection(db),
        orderBy(
          "createdAt",
          "desc"
        )
      );

    const unsubscribe =
      onSnapshot(
        postsQuery,
        (snapshot) => {
          setPosts(
            snapshot.docs.map(
              (postDoc) => ({
                id: postDoc.id,
                ...postDoc.data(),
              })
            )
          );
        }
      );

    return () => unsubscribe();
  }, []);

  useEffect(() => {
    const uids = Array.from(
      new Set(
        posts.flatMap((post) =>
          collectPostIdentityUids(post)
        )
      )
    );

    if (!uids.length) {
      setLiveUsernamesByUid({});
      return undefined;
    }

    const unsubscribers =
      uids.map((userId) =>
        onSnapshot(
          doc(db, "uids", userId),
          (snapshot) => {
            const userData =
              snapshot.data() || {};

            const nextUsername =
              pickFirstText(
                userData.username,
                userData.userName,
                userData.displayName,
                userData.name
              );

            setLiveUsernamesByUid(
              (previous) => {
                const current =
                  previous[userId] ||
                  "";

                if (
                  current ===
                  nextUsername
                ) {
                  return previous;
                }

                if (!nextUsername) {
                  if (
                    !(userId in previous)
                  ) {
                    return previous;
                  }

                  const next = {
                    ...previous,
                  };

                  delete next[userId];
                  return next;
                }

                return {
                  ...previous,
                  [userId]:
                    nextUsername,
                };
              }
            );
          }
        )
      );

    return () => {
      unsubscribers.forEach(
        (unsubscribe) =>
          unsubscribe()
      );
    };
  }, [posts]);

  useEffect(() => {
    if (!teamId) {
      setMyTeamMemberUids([]);
      setMyTeamLeaderId(null);
      return;
    }

    const loadTeam =
      async () => {
        try {
          const snapshot =
            await getDoc(
              doc(
                db,
                "teams",
                teamId
              )
            );

          const team =
            snapshot.exists()
              ? snapshot.data()
              : {};

          setMyTeamMemberUids(
            collectTeamMemberIds(
              team
            )
          );

          setMyTeamLeaderId(
            team.teamLeaderId ||
              null
          );
        } catch (error) {
          console.error(error);
        }
      };

    loadTeam();
  }, [teamId]);

  useEffect(() => {
    if (!uid) {
      setChatsUser([]);
      setChatsTeam([]);
      return undefined;
    }

    const userChatQuery =
      query(
        lfgChatsCollection(db),
        where(
          "participants.user",
          "array-contains",
          uid
        ),
        orderBy(
          "lastMessageAt",
          "desc"
        ),
        limit(25)
      );

    const teamChatQuery =
      query(
        lfgChatsCollection(db),
        where(
          "participants.team",
          "array-contains",
          uid
        ),
        orderBy(
          "lastMessageAt",
          "desc"
        ),
        limit(25)
      );

    const unsubscribeUser =
      onSnapshot(
        userChatQuery,
        (snapshot) => {
          setChatsUser(
            snapshot.docs.map(
              (chatDoc) => ({
                id: chatDoc.id,
                ...chatDoc.data(),
              })
            )
          );
        }
      );

    const unsubscribeTeam =
      onSnapshot(
        teamChatQuery,
        (snapshot) => {
          setChatsTeam(
            snapshot.docs.map(
              (chatDoc) => ({
                id: chatDoc.id,
                ...chatDoc.data(),
              })
            )
          );
        }
      );

    return () => {
      unsubscribeUser();
      unsubscribeTeam();
    };
  }, [uid]);

  useEffect(() => {
    const closeMenu = () =>
      setOpenMenuForPostId(
        null
      );

    document.addEventListener(
      "click",
      closeMenu
    );

    return () =>
      document.removeEventListener(
        "click",
        closeMenu
      );
  }, []);

  useEffect(() => {
    try {
      window.localStorage.setItem(
        LFG_SAVED_POSTS_STORAGE_KEY,
        JSON.stringify(
          savedPostIds
        )
      );
    } catch (_) {
      // Local bookmarking is optional.
    }
  }, [savedPostIds]);

  const filteredPosts =
    useMemo(() => {
      const typeSet =
        new Set(filters.types);

      const regionSet =
        new Set(
          filters.regions.map(
            (region) =>
              region.toLowerCase()
          )
        );

      const rankSet =
        new Set(filters.ranks);

      const rolesSet =
        new Set(filters.roles);

      const search =
        filters.q
          .trim()
          .toLowerCase();

      const result =
        resolvedPosts.filter(
          (post) => {
          if (
            !canViewerSeePost(
              post,
              uid,
              viewerTeamIds
            )
          ) {
            return false;
          }

          if (
            typeSet.size &&
            !typeSet.has(post.type)
          ) {
            return false;
          }

          if (regionSet.size) {
            const region =
              post.type === "team"
                ? post.teamRegion ||
                  ""
                : post.userRegion ||
                  "";

            if (
              !regionSet.has(
                region.toLowerCase()
              )
            ) {
              return false;
            }
          }

          if (rankSet.size) {
            if (
              post.type === "team" &&
              !rankSet.has(
                post.currentAvgRank ||
                  ""
              )
            ) {
              return false;
            }

            if (
              post.type === "user" &&
              !rankSet.has(
                post.currentRank ||
                  ""
              )
            ) {
              return false;
            }
          }

          if (
            rolesSet.size &&
            post.type === "team"
          ) {
            const looking =
              new Set(
                post.lookingForRoles ||
                  []
              );

            for (const role of rolesSet) {
              if (!looking.has(role)) {
                return false;
              }
            }
          }

          if (search) {
            const blob =
              JSON.stringify(
                post
              ).toLowerCase();

            if (
              !blob.includes(search)
            ) {
              return false;
            }
          }

          if (
            post.isOpen !== false
          ) {
            return true;
          }

          return (
            filters.showHiddenMine &&
            canManagePost(
              post,
              uid,
              teamId || null
            )
          );
          }
        );

      return [...result].sort(
        (first, second) => {
          const firstTime =
            first.createdAt?.toMillis?.() ||
            0;

          const secondTime =
            second.createdAt?.toMillis?.() ||
            0;

          return sortOrder ===
            "oldest"
            ? firstTime -
                secondTime
            : secondTime -
                firstTime;
        }
      );
    }, [
      resolvedPosts,
      filters,
      uid,
      teamId,
      viewerTeamIds,
      sortOrder,
    ]);

  const counts =
    useMemo(
      () => ({
        all: filteredPosts.length,

        teams:
          filteredPosts.filter(
            (post) =>
              post.type === "team"
          ).length,

        players:
          filteredPosts.filter(
            (post) =>
              post.type === "user"
          ).length,
      }),
      [filteredPosts]
    );

  const toggleSaved =
    useCallback((postId) => {
      setSavedPostIds(
        (previous) =>
          previous.includes(postId)
            ? previous.filter(
                (id) =>
                  id !== postId
              )
            : [
                ...previous,
                postId,
              ]
      );
    }, []);

  const openEditor =
    useCallback((post) => {
      setOpenMenuForPostId(null);
      setDetailPost(null);

      setEditorState({
        type: post.type,
        post,
      });

      setActiveSection(
        post.type === "team"
          ? "edit-team"
          : "edit-player"
      );
    }, []);

  const openPostDetail =
    useCallback((post) => {
      setOpenMenuForPostId(null);
      setDetailPost(post);
    }, []);

  const openChat = useCallback(
    (conversation) => {
      const teamUids =
        conversation.participants
          ?.team || [];

      const prefixMap =
        conversation.teamName
          ? Object.fromEntries(
              teamUids.map(
                (teamUid) => [
                  teamUid,
                  conversation.teamName,
                ]
              )
            )
          : {};

      setChat({
        open: true,
        id: conversation.id,
        teamMap: prefixMap,
      });

      setActiveSection(
        "messages"
      );
    },
    []
  );

  const deleteChat =
    useCallback(
      async (conversation) => {
        if (!auth.currentUser) {
          return;
        }

        setConfirm({
          open: true,

          message:
            "Delete this conversation for everyone?\n\nThis cannot be undone.",

          onConfirm: async () => {
            try {
              const messagesCollection =
                lfgChatMessagesCollection(
                  db,
                  conversation.id
                );

              while (true) {
                const page =
                  await getDocs(
                    query(
                      messagesCollection,
                      orderBy(
                        "createdAt",
                        "asc"
                      ),
                      limit(300)
                    )
                  );

                if (page.empty) {
                  break;
                }

                await Promise.all(
                  page.docs.map(
                    (messageDoc) =>
                      deleteDoc(
                        messageDoc.ref
                      )
                  )
                );

                if (page.size < 300) {
                  break;
                }
              }

              await deleteDoc(
                lfgChatDoc(
                  db,
                  conversation.id
                )
              );

              setChat(
                (current) =>
                  current.id ===
                  conversation.id
                    ? {
                        open: false,
                        id: null,
                        teamMap: {},
                      }
                    : current
              );

              setChatsUser(
                (previous) =>
                  previous.filter(
                    (item) =>
                      item.id !==
                      conversation.id
                  )
              );

              setChatsTeam(
                (previous) =>
                  previous.filter(
                    (item) =>
                      item.id !==
                      conversation.id
                  )
              );

              showToast(
                "Conversation deleted.",
                "success"
              );
            } catch (error) {
              showError(error);
            }
          },
        });
      },
      [
        showError,
        showToast,
      ]
    );

  const startConnection =
    async (post) => {
      try {
        if (!auth.currentUser) {
          showToast(
            "Login required.",
            "warning"
          );

          return;
        }

        let teamSideIds = [];
        let teamLabel = null;
        const teamQuestions =
          post.type === "team"
            ? normalizeQuestionList(
                post.customQuestions
              )
            : [];

        const direction =
          post.type === "team"
            ? "player_to_team"
            : "team_to_player";

        if (post.type === "team") {
          teamLabel =
            post.teamName ||
            "Team";

          teamSideIds =
            Array.from(
              new Set([
                ...collectTeamMemberIds(
                  post,
                  post.teamMembers
                ),
                post.createdBy,
              ])
            ).filter(Boolean);
        } else {
          if (!teamId) {
            showToast(
              "Join or create a team first to connect as a team.",
              "warning"
            );

            return;
          }

          const teamSnapshot =
            await getDoc(
              doc(
                db,
                "teams",
                teamId
              )
            );

          const team =
            teamSnapshot.exists()
              ? teamSnapshot.data()
              : {};

          teamLabel =
            team.teamName ||
            teamName ||
            "Team";

          teamSideIds =
            Array.from(
              new Set([
                ...collectTeamMemberIds(
                  team
                ),
                team.teamLeaderId,
                auth.currentUser.uid,
              ])
            ).filter(Boolean);
        }

        const userSideId =
          post.type === "user"
            ? post.userId
            : auth.currentUser.uid;

        const getUsername =
          async (userId) => {
            const snapshot =
              await getDoc(
                doc(
                  db,
                  "uids",
                  userId
                )
              );

            return snapshot.exists()
              ? snapshot.data()
                  .username ||
                  "Unknown"
              : "Unknown";
          };

        const playerUsername =
          post.type === "user" &&
          post.username
            ? post.username
            : await getUsername(
                userSideId
              );

        let existingChat = null;

        try {
          const existingQuery =
            await getDocs(
              query(
                lfgChatsCollection(db),
                where(
                  "participants.user",
                  "array-contains",
                  userSideId
                ),
                where(
                  "teamId",
                  "==",
                  post.type ===
                    "team"
                    ? post.teamId
                    : teamId
                ),
                limit(1)
              )
            );

          existingChat =
            existingQuery.docs[0] ||
            null;
        } catch (_) {
          existingChat = null;
        }

        let chatId;

        if (existingChat) {
          chatId =
            existingChat.id;

          await updateDoc(
            lfgChatDoc(
              db,
              chatId
            ),
            {
              lastMessageAt:
                serverTimestamp(),
              ...(post.type === "team"
                ? {
                    teamQuestions,
                  }
                : {}),
            }
          );
        } else {
          const chatReference =
            await addDoc(
              lfgChatsCollection(db),
              {
                teamId:
                  post.type ===
                  "team"
                    ? post.teamId
                    : teamId,

                teamName:
                  teamLabel,

                startedBy: {
                  type:
                    post.type ===
                    "team"
                      ? "user"
                      : "team",

                  id:
                    auth.currentUser
                      .uid,
                },

                direction,

                teamQuestions,

                teamPostId:
                  post.type ===
                  "team"
                    ? post.id
                    : null,

                userPostId:
                  post.type ===
                  "user"
                    ? post.id
                    : null,

                playerId:
                  userSideId,

                playerUsername,

                participants: {
                  team:
                    teamSideIds,

                  user: [
                    userSideId,
                  ],
                },

                createdAt:
                  serverTimestamp(),

                lastMessageAt:
                  serverTimestamp(),
              }
            );

          chatId =
            chatReference.id;
        }

        const prefixMap =
          Object.fromEntries(
            teamSideIds.map(
              (teamUid) => [
                teamUid,
                teamLabel,
              ]
            )
          );

        setChat({
          open: true,
          id: chatId,
          teamMap: prefixMap,
        });

        setActiveSection(
          "messages"
        );
      } catch (error) {
        showError(error);
      }
    };

  const submitReport =
    async () => {
      try {
        if (!auth.currentUser) {
          showToast(
            "Login required to report posts.",
            "warning"
          );

          return;
        }

        if (!reportModal.post) {
          return;
        }

        if (!reportModal.reason) {
          showToast(
            "Please select a report reason.",
            "warning"
          );

          return;
        }

        if (
          reportModal.reason ===
            "Other" &&
          !reportModal.otherText.trim()
        ) {
          showToast(
            "Please write a short message for Other.",
            "warning"
          );

          return;
        }

        const reporterId =
          auth.currentUser.uid;

        let reporterUsername =
          "Unknown";

        try {
          const userSnapshot =
            await getDoc(
              doc(
                db,
                "users",
                reporterId
              )
            );

          if (
            userSnapshot.exists()
          ) {
            reporterUsername =
              userSnapshot.data()
                .username ||
              "Unknown";
          }
        } catch (_) {
          // Optional enrichment.
        }

        const post =
          reportModal.post;

        const payload = {
          status: "submitted",

          createdAt:
            serverTimestamp(),

          reporterId,

          reporterUsername,

          postId: post.id,

          postType: post.type,

          postOwnerId:
            post.type === "user"
              ? post.userId
              : post.createdBy ||
                null,

          postTeamId:
            post.type === "team"
              ? post.teamId ||
                null
              : null,

          postDisplayName:
            postDisplayName(post),

          reason:
            reportModal.reason,

          otherText:
            reportModal.reason ===
            "Other"
              ? reportModal.otherText
                  .trim()
                  .slice(0, 200)
              : "",
        };

        await addDoc(
          feedbackReportsCollection(db),
          payload
        );

        setSubmittedReports(
          (previous) => [
            {
              ...payload,
              createdAt:
                new Date(),
            },
            ...previous,
          ]
        );

        showToast(
          "Report submitted. Thank you.",
          "success"
        );

        setReportModal({
          open: false,
          post: null,
          reason: "",
          otherText: "",
        });
      } catch (error) {
        showError(error);
      }
    };

  const importPlayerAvailabilityToTeam =
    useCallback(
      (playerPost) => {
        if (!teamId) {
          showToast(
            "Join or create a team first.",
            "warning"
          );

          return;
        }

        if (
          playerPost.type !==
          "user"
        ) {
          return;
        }

        if (
          !playerPost.availabilityShared
        ) {
          showToast(
            "This player has not attached availability.",
            "warning"
          );

          return;
        }

        setImportAvailModal({
          open: true,

          playerPost,

          dateISO:
            new Date()
              .toISOString()
              .slice(0, 10),

          loading: false,
        });
      },
      [
        teamId,
        showToast,
      ]
    );

  const isChattingWithPost =
    useCallback(
      (post) => {
        if (!myChats.length) {
          return false;
        }

        if (post.type === "team") {
          if (!uid) {
            return false;
          }

          return myChats.some(
            (conversation) =>
              conversation.teamId ===
                post.teamId &&
              conversation.playerId ===
                uid
          );
        }

        if (!teamId) {
          return false;
        }

        return myChats.some(
          (conversation) =>
            conversation.teamId ===
              teamId &&
            conversation.playerId ===
              post.userId
        );
      },
      [
        myChats,
        uid,
        teamId,
      ]
    );

  const renderFeedPost =
    (post) => (
      <LFGPostCard
        key={post.id}
        post={post}
        viewerUid={uid}
        currentTeamId={
          teamId || null
        }
        teamMemberUids={
          myTeamMemberUids
        }
        chattingAlready={isChattingWithPost(
          post
        )}
        saved={savedPostIds.includes(
          post.id
        )}
        menuOpen={
          openMenuForPostId ===
          post.id
        }
        onToggleMenu={
          setOpenMenuForPostId
        }
        onReport={(selectedPost) =>
          setReportModal({
            open: true,
            post: selectedPost,
            reason: "",
            otherText: "",
          })
        }
        onToggleSaved={
          toggleSaved
        }
        onConnect={
          startConnection
        }
        onViewDetails={
          openPostDetail
        }
        onEdit={openEditor}
        onToggleOpen={(
          selectedPost,
          next
        ) =>
          toggleOpen(
            selectedPost,
            next,
            showToast
          )
        }
        onImportAvailability={
          importPlayerAvailabilityToTeam
        }
      />
    );

  const renderFeed = () => (
    <>
      <header className="--LFGV2-main-header">
        <div className="--LFGV2-main-header__intro">
          <span className="--LFGV2-page-kicker">
            Recruitment Hub
          </span>

          <h1 className="--LFGV2-page-title">
            Looking For Group
          </h1>

          <p className="--LFGV2-page-subtitle">
            Find teammates. Build your
            team. Compete together.
          </p>
        </div>

        <div className="--LFGV2-main-header__actions">
          {teamId && (
            <button
              type="button"
              className="--LFGV2-btn --LFGV2-btn--team"
              onClick={() => {
                setEditorState({
                  type: "team",
                  post: null,
                });

                setActiveSection(
                  "create-team"
                );
              }}
            >
              + Create Team Post
            </button>
          )}

          <button
            type="button"
            className="--LFGV2-btn --LFGV2-btn--player"
            onClick={() => {
              setEditorState({
                type: "user",
                post: null,
              });

              setActiveSection(
                "create-player"
              );
            }}
          >
            + Create Player Post
          </button>
        </div>
      </header>

      <LFGFilters
        filters={filters}
        onChange={setFilters}
        onToggleShowHiddenMine={
          applyShowHiddenMine
        }
      />

      <div className="--LFGV2-feed-toolbar">
        <div className="--LFGV2-tabs">
          <button
            type="button"
            className={
              filters.types.length === 0
                ? "--LFGV2-tab --LFGV2-tab--active"
                : "--LFGV2-tab"
            }
            onClick={() =>
              setFilters({
                ...filters,
                types: [],
              })
            }
          >
            All Posts
            <span>
              {counts.all}
            </span>
          </button>

          <button
            type="button"
            className={
              filters.types.length ===
                1 &&
              filters.types[0] ===
                "team"
                ? "--LFGV2-tab --LFGV2-tab--active"
                : "--LFGV2-tab"
            }
            onClick={() =>
              setFilters({
                ...filters,
                types: ["team"],
              })
            }
          >
            Teams
            <span>
              {counts.teams}
            </span>
          </button>

          <button
            type="button"
            className={
              filters.types.length ===
                1 &&
              filters.types[0] ===
                "user"
                ? "--LFGV2-tab --LFGV2-tab--active"
                : "--LFGV2-tab"
            }
            onClick={() =>
              setFilters({
                ...filters,
                types: ["user"],
              })
            }
          >
            Players
            <span>
              {counts.players}
            </span>
          </button>
        </div>

        <select
          className="--LFGV2-sort"
          value={sortOrder}
          onChange={(event) =>
            setSortOrder(
              event.target.value
            )
          }
        >
          <option value="newest">
            Newest First
          </option>

          <option value="oldest">
            Oldest First
          </option>
        </select>
      </div>

      <div className="--LFGV2-feed-layout">
        <section className="--LFGV2-feed">
          {filteredPosts.length ? (
            filteredPosts.map(
              renderFeedPost
            )
          ) : (
            <EmptyState
              title="No LFG posts found"
              description="Try removing a filter or create a new post."
            />
          )}
        </section>

      </div>
    </>
  );

  const renderMyPosts =
    () => (
      <div className="--LFGV2-subpage">
        <header className="--LFGV2-section-header">
          <div>
            <span className="--LFGV2-page-kicker">
              Manage Recruitment
            </span>

            <h1 className="--LFGV2-page-title">
              My Posts
            </h1>

            <p className="--LFGV2-page-subtitle">
              Edit, hide, unhide or
              permanently delete your
              eligible LFG posts.
            </p>
          </div>
        </header>

        <div className="--LFGV2-management-grid">
          <section className="--LFGV2-management-card">
            <div className="--LFGV2-card-header">
              <h2>
                Visible Posts
              </h2>

              <span className="--LFGV2-count-badge">
                {
                  managedPosts.filter(
                    (post) =>
                      post.isOpen !==
                      false
                  ).length
                }
              </span>
            </div>

            {managedPosts.filter(
              (post) =>
                post.isOpen !== false
            ).length ? (
              managedPosts
                .filter(
                  (post) =>
                    post.isOpen !==
                    false
                )
                .map((post) => (
                  <div
                    key={post.id}
                    className="--LFGV2-management-row"
                  >
                    <div className="--LFGV2-management-row__identity">
                      <Avatar
                        name={postDisplayName(
                          post
                        )}
                        size="small"
                      />

                      <div>
                        <strong>
                          {postDisplayName(
                            post
                          )}
                        </strong>

                        <span>
                          {post.type ===
                          "team"
                            ? "Team Post"
                            : "Player Post"}
                        </span>
                      </div>
                    </div>

                    <div className="--LFGV2-management-row__actions">
                      <button
                        type="button"
                        className="--LFGV2-btn --LFGV2-btn--secondary"
                        onClick={() =>
                          openEditor(
                            post
                          )
                        }
                      >
                        Edit
                      </button>

                      <button
                        type="button"
                        className="--LFGV2-btn --LFGV2-btn--ghost"
                        onClick={() =>
                          toggleOpen(
                            post,
                            false,
                            showToast
                          )
                        }
                      >
                        Hide
                      </button>
                    </div>
                  </div>
                ))
            ) : (
              <EmptyState
                title="No visible posts"
                description="Your active recruitment posts will appear here."
              />
            )}
          </section>

          <section className="--LFGV2-management-card">
            <div className="--LFGV2-card-header">
              <h2>
                Hidden Posts
              </h2>

              <span className="--LFGV2-count-badge --LFGV2-count-badge--danger">
                {
                  managedPosts.filter(
                    (post) =>
                      post.isOpen ===
                      false
                  ).length
                }
              </span>
            </div>

            {managedPosts.filter(
              (post) =>
                post.isOpen === false
            ).length ? (
              managedPosts
                .filter(
                  (post) =>
                    post.isOpen ===
                    false
                )
                .map((post) => (
                  <div
                    key={post.id}
                    className="--LFGV2-management-row"
                  >
                    <div className="--LFGV2-management-row__identity">
                      <Avatar
                        name={postDisplayName(
                          post
                        )}
                        size="small"
                      />

                      <div>
                        <strong>
                          {postDisplayName(
                            post
                          )}
                        </strong>

                        <span>
                          Hidden
                        </span>
                      </div>
                    </div>

                    <div className="--LFGV2-management-row__actions">
                      <button
                        type="button"
                        className="--LFGV2-btn --LFGV2-btn--secondary"
                        onClick={() =>
                          toggleOpen(
                            post,
                            true,
                            showToast
                          )
                        }
                      >
                        Unhide
                      </button>

                      {canDeletePost(
                        post,
                        uid,
                        myTeamLeaderId
                      ) && (
                        <button
                          type="button"
                          className="--LFGV2-btn --LFGV2-btn--danger"
                          onClick={() =>
                            setConfirm(
                              {
                                open: true,

                                message: `Delete this LFG permanently?\n\n${postDisplayName(
                                  post
                                )}`,

                                onConfirm:
                                  () =>
                                    reallyDeletePost(
                                      post,
                                      showError
                                    ),
                              }
                            )
                          }
                        >
                          Delete
                        </button>
                      )}
                    </div>
                  </div>
                ))
            ) : (
              <EmptyState
                title="No hidden posts"
                description="Posts you hide will remain available here."
              />
            )}
          </section>
        </div>
      </div>
    );

  const renderMessages =
    () => (
      <div className="--LFGV2-subpage --LFGV2-subpage--messages">
        <header className="--LFGV2-section-header">
          <div>
            <span className="--LFGV2-page-kicker">
              Direct Recruitment
            </span>

            <h1 className="--LFGV2-page-title">
              Messages
            </h1>
          </div>
        </header>

        <div className="--LFGV2-messages-layout">
          <aside className="--LFGV2-inbox-panel">
            <div className="--LFGV2-inbox-panel__header">
              <div>
                <h2>
                  My LFG Chats
                </h2>

                <span>
                  {myChats.length}{" "}
                  total
                </span>
              </div>
            </div>

            <div className="--LFGV2-inbox-search">
              <span>
                ⌕
              </span>

              <input
                className="--LFGV2-input"
                placeholder="Search chats..."
                readOnly
              />
            </div>

            <ChatList
              chats={myChats}
              selectedChatId={
                chat.id
              }
              onOpen={openChat}
              onDelete={deleteChat}
            />
          </aside>

          <LFGChatRoom
            chatId={
              chat.open
                ? chat.id
                : null
            }
            teamNamePrefixMap={
              chat.teamMap
            }
            onClose={() =>
              setChat({
                open: false,
                id: null,
                teamMap: {},
              })
            }
            onDelete={() => {
              const conversation =
                myChats.find(
                  (item) =>
                    item.id ===
                    chat.id
                );

              if (conversation) {
                deleteChat(
                  conversation
                );
              }
            }}
            onViewPost={(
              postId
            ) => {
              const post =
                resolvedPosts.find(
                  (item) =>
                    item.id ===
                    postId
                );

              if (
                post &&
                canViewerSeePost(
                  post,
                  uid,
                  viewerTeamIds
                )
              ) {
                openPostDetail(
                  post
                );
                return;
              }

              if (post) {
                showToast(
                  "This player post is hidden from your teams.",
                  "info"
                );
              }
            }}
            showToast={showToast}
            showError={showError}
          />
        </div>
      </div>
    );

  const renderSaved =
    () => (
      <div className="--LFGV2-subpage">
        <header className="--LFGV2-section-header">
          <div>
            <span className="--LFGV2-page-kicker">
              Bookmarks
            </span>

            <h1 className="--LFGV2-page-title">
              Saved Posts
            </h1>

            <p className="--LFGV2-page-subtitle">
              LFG posts you've saved
              on this device.
            </p>
          </div>
        </header>

        <section className="--LFGV2-feed">
          {savedPosts.length ? (
            savedPosts.map(
              renderFeedPost
            )
          ) : (
            <EmptyState
              title="Nothing saved yet"
              description="Use the star button on a team or player post to keep it here."
            />
          )}
        </section>
      </div>
    );

  const renderReports =
    () => (
      <div className="--LFGV2-subpage">
        <header className="--LFGV2-section-header">
          <div>
            <span className="--LFGV2-page-kicker">
              Safety
            </span>

            <h1 className="--LFGV2-page-title">
              Reports
            </h1>

            <p className="--LFGV2-page-subtitle">
              Reports submitted during
              this session are shown
              below.
            </p>
          </div>
        </header>

        <section className="--LFGV2-management-card">
          {submittedReports.length ? (
            submittedReports.map(
              (report, index) => (
                <div
                  key={`${report.postId}-${index}`}
                  className="--LFGV2-report-row"
                >
                  <div>
                    <strong>
                      {
                        report.postDisplayName
                      }
                    </strong>

                    <span>
                      {
                        report.reason
                      }
                    </span>
                  </div>

                  <MetadataBadge variant="success">
                    Submitted
                  </MetadataBadge>
                </div>
              )
            )
          ) : (
            <EmptyState
              title="No reports this session"
              description="You can report a post from its three-dot menu."
            />
          )}
        </section>
      </div>
    );

  const renderSettings =
    () => (
      <div className="--LFGV2-subpage">
        <header className="--LFGV2-section-header">
          <div>
            <span className="--LFGV2-page-kicker">
              Preferences
            </span>

            <h1 className="--LFGV2-page-title">
              LFG Settings
            </h1>
          </div>
        </header>

        <div className="--LFGV2-settings-grid">
          <section className="--LFGV2-settings-card">
            <h2>
              Feed Preferences
            </h2>

            <label className="--LFGV2-settings-row">
              <div>
                <strong>
                  Show my hidden posts
                </strong>

                <span>
                  Allow hidden posts you
                  manage to appear in
                  your LFG feed.
                </span>
              </div>

              <input
                type="checkbox"
                checked={
                  filters.showHiddenMine
                }
                onChange={(event) =>
                  applyShowHiddenMine(
                    event.target.checked
                  )
                }
              />
            </label>
          </section>

          <section className="--LFGV2-settings-card">
            <h2>
              Message Retention
            </h2>

            <div className="--LFGV2-settings-row">
              <div>
                <strong>
                  14-day LFG messages
                </strong>

                <span>
                  New LFG messages are
                  created with a
                  deletion date 14 days
                  after they are sent.
                </span>
              </div>

              <MetadataBadge>
                14 Days
              </MetadataBadge>
            </div>
          </section>

          <section className="--LFGV2-settings-card">
            <h2>
              Saved Posts
            </h2>

            <div className="--LFGV2-settings-row">
              <div>
                <strong>
                  Local bookmarks
                </strong>

                <span>
                  Saved post IDs are
                  stored on this device.
                </span>
              </div>

              <MetadataBadge>
                {savedPostIds.length}
              </MetadataBadge>
            </div>
          </section>
        </div>
      </div>
    );

  const renderContent = () => {
    if (
      activeSection ===
      "create-player"
    ) {
      return (
        <PlayerPostEditor
          onCancel={() =>
            setActiveSection("feed")
          }
          onSaved={() =>
            setActiveSection("feed")
          }
          showToast={showToast}
          showError={showError}
        />
      );
    }

    if (
      activeSection ===
      "edit-player"
    ) {
      return (
        <PlayerPostEditor
          existingPost={
            editorState.post
          }
          onCancel={() =>
            setActiveSection("feed")
          }
          onSaved={() =>
            setActiveSection("feed")
          }
          showToast={showToast}
          showError={showError}
        />
      );
    }

    if (
      activeSection ===
      "create-team"
    ) {
      return (
        <TeamPostEditor
          teamId={teamId}
          onCancel={() =>
            setActiveSection("feed")
          }
          onSaved={() =>
            setActiveSection("feed")
          }
          showToast={showToast}
          showError={showError}
        />
      );
    }

    if (
      activeSection ===
      "edit-team"
    ) {
      return (
        <TeamPostEditor
          teamId={
            editorState.post
              ?.teamId || teamId
          }
          existingPost={
            editorState.post
          }
          onCancel={() =>
            setActiveSection("feed")
          }
          onSaved={() =>
            setActiveSection("feed")
          }
          showToast={showToast}
          showError={showError}
        />
      );
    }

    if (
      activeSection ===
      "my-posts"
    ) {
      return renderMyPosts();
    }

    if (
      activeSection ===
      "messages"
    ) {
      return renderMessages();
    }

    if (
      activeSection === "saved"
    ) {
      return renderSaved();
    }

    if (
      activeSection ===
      "reports"
    ) {
      return renderReports();
    }

    if (
      activeSection ===
      "settings"
    ) {
      return renderSettings();
    }

    return renderFeed();
  };

  return (
    <section className="--LFGV2-page">
      {toast.open && (
        <CustomAlert
          position="top"
          type={toast.type}
          message={toast.message}
          onClose={() =>
            setToast({
              open: false,
              message: "",
              type: "info",
            })
          }
        />
      )}

      <div className="--LFGV2-shell">
        <Sidebar
          activeSection={
            activeSection
          }
          onNavigate={(section) => {
            if (
              section === "settings" &&
              isDesktopShell &&
              window.desktopAPI
                ?.openSettingsWindow
            ) {
              void window.desktopAPI.openSettingsWindow(
                {
                  section: "lfg",
                }
              );
              return;
            }

            setActiveSection(section);
            setDetailPost(null);
          }}
          chatCount={
            myChats.length
          }
          savedCount={
            savedPostIds.length
          }
        />

        <main className="--LFGV2-main">
          {renderContent()}
        </main>
      </div>

      <Modal
        open={!!detailPost}
        title={
          detailPost
            ? `${postDisplayName(
                detailPost
              )} Details`
            : "Post Details"
        }
        onClose={() =>
          setDetailPost(null)
        }
        className="--LFGV2-modal--detail"
      >
        {detailPost && (
          <PostDetailView
            post={detailPost}
            viewerUid={uid}
            currentTeamId={
              teamId || null
            }
            teamMemberUids={
              myTeamMemberUids
            }
            chattingAlready={isChattingWithPost(
              detailPost
            )}
            saved={savedPostIds.includes(
              detailPost.id
            )}
            onBack={() =>
              setDetailPost(null)
            }
            onReport={(post) =>
              setReportModal({
                open: true,
                post,
                reason: "",
                otherText: "",
              })
            }
            onToggleSaved={
              toggleSaved
            }
            onConnect={
              startConnection
            }
            onEdit={openEditor}
            onImportAvailability={
              importPlayerAvailabilityToTeam
            }
          />
        )}
      </Modal>

      {confirm.open && (
        <DeleteHighlightConfirm
          message={
            confirm.message ||
            "Are you sure?"
          }
          onConfirm={async () => {
            try {
              await confirm.onConfirm?.();
            } finally {
              setConfirm({
                open: false,
                message: "",
                onConfirm: null,
              });
            }
          }}
          onCancel={() =>
            setConfirm({
              open: false,
              message: "",
              onConfirm: null,
            })
          }
        />
      )}

      <Modal
        open={
          importAvailModal.open
        }
        title="Import Weekly Availability"
        onClose={() =>
          setImportAvailModal({
            open: false,
            playerPost: null,
            dateISO: "",
            loading: false,
          })
        }
      >
        <div className="--LFGV2-modal-stack">
          <div className="--LFGV2-import-player">
            <Avatar
              name={
                importAvailModal
                  .playerPost
                  ?.username ||
                "Player"
              }
              size="medium"
            />

            <div>
              <span>
                Importing from
              </span>

              <strong>
                {importAvailModal
                  .playerPost
                  ?.username ||
                  "Player"}
              </strong>
            </div>
          </div>

          <label className="--LFGV2-field">
            <span>
              Pick any date in the week
            </span>

            <input
              className="--LFGV2-input"
              type="date"
              value={
                importAvailModal.dateISO
              }
              onChange={(event) =>
                setImportAvailModal(
                  (previous) => ({
                    ...previous,

                    dateISO:
                      event.target
                        .value,
                  })
                )
              }
            />
          </label>

          <div className="--LFGV2-info-card">
            Select any day. The import
            will use the full
            Monday-to-Sunday week
            containing that date.
          </div>

          <div className="--LFGV2-modal-actions">
            <button
              type="button"
              className="--LFGV2-btn --LFGV2-btn--secondary app-cancel-control"
              disabled={
                importAvailModal.loading
              }
              onClick={() =>
                setImportAvailModal({
                  open: false,
                  playerPost: null,
                  dateISO: "",
                  loading: false,
                })
              }
            >
              Cancel
            </button>

            <button
              type="button"
              className="--LFGV2-btn --LFGV2-btn--primary"
              disabled={
                importAvailModal.loading
              }
              onClick={async () => {
                const pickedDate =
                  importAvailModal.dateISO;

                if (!pickedDate) {
                  showToast(
                    "Please choose a date.",
                    "warning"
                  );

                  return;
                }

                const parts =
                  pickedDate
                    .split("-")
                    .map(Number);

                if (
                  parts.length !== 3
                ) {
                  showToast(
                    "Invalid date format.",
                    "warning"
                  );

                  return;
                }

                const anyDate =
                  new Date(
                    parts[0],
                    parts[1] - 1,
                    parts[2]
                  );

                if (
                  Number.isNaN(
                    +anyDate
                  )
                ) {
                  showToast(
                    "Invalid date.",
                    "warning"
                  );

                  return;
                }

                try {
                  setImportAvailModal(
                    (previous) => ({
                      ...previous,
                      loading: true,
                    })
                  );

                  const weekStart =
                    mondayOf(
                      anyDate
                    );

                  const weekEnd =
                    new Date(
                      weekStart
                    );

                  weekEnd.setDate(
                    weekEnd.getDate() +
                      6
                  );

                  weekEnd.setHours(
                    23,
                    59,
                    59,
                    999
                  );

                  const {
                    importSharedAvailToTeam,
                  } = await import(
                    "../../components/Calendar/_availabilityShared"
                  );

                  const player =
                    importAvailModal.playerPost;

                  const count =
                    await importSharedAvailToTeam(
                      {
                        teamId,

                        playerUserId:
                          player.userId,

                        playerUsername:
                          player.username,

                        weekStart,

                        weekEnd,
                      }
                    );

                  if (count > 0) {
                    showToast(
                      `Imported ${count} block(s) from ${player.username}.`,
                      "success"
                    );
                  } else {
                    showToast(
                      "No availability blocks found for that week.",
                      "info"
                    );
                  }

                  setImportAvailModal({
                    open: false,
                    playerPost: null,
                    dateISO: "",
                    loading: false,
                  });
                } catch (error) {
                  showError(error);

                  setImportAvailModal(
                    (previous) => ({
                      ...previous,
                      loading: false,
                    })
                  );
                }
              }}
            >
              {importAvailModal.loading
                ? "Importing..."
                : "Import Availability"}
            </button>
          </div>
        </div>
      </Modal>

      <Modal
        open={reportModal.open}
        title="Report Post"
        onClose={() =>
          setReportModal({
            open: false,
            post: null,
            reason: "",
            otherText: "",
          })
        }
      >
        <div className="--LFGV2-modal-stack">
          <div className="--LFGV2-report-target">
            <Avatar
              name={
                reportModal.post
                  ? postDisplayName(
                      reportModal.post
                    )
                  : "Post"
              }
              size="medium"
            />

            <div>
              <span>
                Reporting
              </span>

              <strong>
                {reportModal.post
                  ? postDisplayName(
                      reportModal.post
                    )
                  : "Post"}
              </strong>
            </div>
          </div>

          <div className="--LFGV2-radio-list">
            {REPORT_REASONS.map(
              (reason) => (
                <label
                  key={reason}
                  className={
                    reportModal.reason ===
                    reason
                      ? "--LFGV2-radio-option --LFGV2-radio-option--active"
                      : "--LFGV2-radio-option"
                  }
                >
                  <input
                    type="radio"
                    name="reportReason"
                    value={reason}
                    checked={
                      reportModal.reason ===
                      reason
                    }
                    onChange={() =>
                      setReportModal(
                        (previous) => ({
                          ...previous,
                          reason,
                        })
                      )
                    }
                  />

                  <span>
                    {reason}
                  </span>
                </label>
              )
            )}
          </div>

          {reportModal.reason ===
            "Other" && (
            <label className="--LFGV2-field">
              <span>
                Additional details
                <small>
                  Maximum 200
                  characters
                </small>
              </span>

              <textarea
                className="--LFGV2-input --LFGV2-textarea"
                value={
                  reportModal.otherText
                }
                onChange={(event) =>
                  setReportModal(
                    (previous) => ({
                      ...previous,

                      otherText:
                        event.target.value.slice(
                          0,
                          200
                        ),
                    })
                  )
                }
                onKeyDownCapture={
                  stopSpacePropagation
                }
                placeholder="Briefly explain the issue..."
              />

              <span className="--LFGV2-character-count">
                {
                  reportModal
                    .otherText
                    .length
                }
                /200
              </span>
            </label>
          )}

          <div className="--LFGV2-modal-actions">
            <button
              type="button"
              className="--LFGV2-btn --LFGV2-btn--secondary app-cancel-control"
              onClick={() =>
                setReportModal({
                  open: false,
                  post: null,
                  reason: "",
                  otherText: "",
                })
              }
            >
              Cancel
            </button>

            <button
              type="button"
              className="--LFGV2-btn --LFGV2-btn--danger"
              onClick={
                submitReport
              }
            >
              Submit Report
            </button>
          </div>
        </div>
      </Modal>
    </section>
  );
}
